import React from "react";
import { Map, View } from "ol";
import proj4 from "proj4";
import TileLayer from "ol/layer/Tile";
import XYZ from "ol/source/XYZ.js";
import TileGrid from "ol/tilegrid/TileGrid";
import { register } from "ol/proj/proj4.js";
import MousePosition from "ol/control/MousePosition.js";
import { createStringXY } from "ol/coordinate.js";
import { get as getProjection } from "ol/proj.js";
import VectorSource from "ol/source/Vector.js";
import { Stroke, Style, Fill, Circle } from "ol/style.js";
import VectorLayer from "ol/layer/Vector.js";
import GeoJSON from "ol/format/GeoJSON.js";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import Zoom from "ol/control/Zoom.js";
import Feature from "ol/Feature.js";
import Point from "ol/geom/Point.js";
import { ScaleLine } from "ol/control.js";
import "ol/ol.css";
import Overlay from "ol/Overlay.js";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "./MapViewOl.css";
import { useTranslation } from "react-i18next";
import DestinationToolbar from "./DestinationToolbar";
import SiteToolbar from "./SiteToolbar";
import Select from "ol/interaction/Select.js";
import { platformModifierKeyOnly, singleClick } from "ol/events/condition.js";
import Collection from "ol/Collection.js";
import { AxiosError } from "axios";
import { getCodeText } from "../utils/CodeHelpers";
import * as olExtent from "ol/extent";
import Icon from "ol/style/Icon.js";

import { Control } from "ol/control.js";
import {
  GetMunicipalities,
  GetCounties,
  GetUserCreatedSites,
  GetUserCreatedSite,
  GetUserCreatedDestinations,
  GetUserCreatedDestination,
  GetRegionalUnits,
  GetParkDistricts,
  GetEntities,
  GetLandUseUnits,
  GetCodeClasses,
} from "../utils/NastaApiClient";

import add_fill from "../icons/add_fill.svg";
import subtract_fill from "../icons/subtract_fill.svg";
import view_fill from "../icons/view_fill.svg";
import view_off_fill from "../icons/view-off_fill.svg";
import layers_stacked_fill from "../icons/layers-stacked_fill.svg";
import close_fill from "../icons/close_fill.svg";
import eraser2 from "../icons/Property 1=Line.svg";

import { useSelector, useDispatch, useStore } from "react-redux";
import * as mapLayerSelector from "./mapLayerSelector";
import * as destinationsFilterSelector from "./destinationsFilterSelector";
import * as sitesFilterSelector from "./siteFilterSelector";
import * as mapCommonsSelector from "./mapCommonsSelector";
import * as codesSelector from "./codesSelector";
import * as mapSelectionSelector from "./mapSelectionSelector";
import UploadingNotification from "../widgets/UploadingNotification/UploadingNotification";

const withNavigation = (WrappedComponent) => (props) => {
  const navigation = useNavigate();
  const layers = useSelector((state) => state.layerSelector.layers);
  const destinationFilters = useSelector(
    (state) => state.destinationsFilterSelector
  );
  const siteFilters = useSelector((state) => state.sitesFilterSelector);
  const mapCommons = useSelector((state) => state.mapCommonsSelector);
  const codes = useSelector((state) => state.codes);
  const storedMapSelection = useSelector((state) => state.mapSelectionSelector);
  const dispatch = useDispatch();
  const storeHelper = useStore();
  const location = useLocation();
  const { t } = useTranslation(["mapViewOl"]);
  const [searchParams, setSearchParams] = useSearchParams();

  return (
    <>
      <AuthenticatedTemplate>
        <MapViewOl
          {...props}
          navigation={navigation}
          t={t}
          location={location}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          codes={codes}
          storedMapSelection={storedMapSelection}
          layers={layers}
          storeHelper={storeHelper}
          destinationFilters={destinationFilters}
          siteFilters={siteFilters}
          mapCommons={mapCommons}
          dispatch={dispatch}
        />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate />
    </>
  );
};

proj4.defs(
  "EPSG:3067",
  "+proj=utm +zone=35 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs"
);
proj4.defs(
  "http://www.opengis.net/def/crs/EPSG/0/3067",
  "+proj=utm +zone=35 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs"
);
proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs +type=crs");
register(proj4);

const resolutions = [
  8192, 4096, 2048, 1024, 512, 256, 128, 64, 32, 16, 8, 4, 2, 1, 0.5,
];
const extent = [-548576, 6291456, 1548576, 8388608];
const baseMapLayerName = "baseMapLayer";
const destinationLayerSource = new VectorSource();
const siteSource = new VectorSource();
const landUseUnitSource = new VectorSource();
const temporaryLayerSource = new VectorSource();
const hiddenSiteLayerSource = new VectorSource();
const hiddenDestinationLayerSource = new VectorSource();
const countiesLayerSource = new VectorSource();
const municipalitiesLayerSource = new VectorSource();
const regionalUnitsLayerSource = new VectorSource();
const parkDistrictLayerSource = new VectorSource();
const entityLayerSource = new VectorSource();
const mapFeatureSelection = new Collection();
const proj3067 = getProjection("EPSG:3067");

proj3067.setExtent(extent);

const proj3067Alias = getProjection(
  "http://www.opengis.net/def/crs/EPSG/0/3067"
);
proj3067Alias.setExtent(extent);

const mousePositionControl = new MousePosition({
  coordinateFormat: createStringXY(),
  projection: "EPSG:3067",
  className: "custom-mouse-position coordinate-viewer",
  target: document.getElementById("mouse-position"),
});

const precisionInput = document.getElementById("precision");
if (typeof precisionInput !== "undefined" && precisionInput !== null) {
  precisionInput.addEventListener("change", function (event) {
    const format = createStringXY(event.target.valueAsNumber);
    mousePositionControl.setCoordinateFormat(format);
  });
}

const countiesLayer = new VectorLayer({
  zIndex: 3,
  name: "countyLayer",
  source: countiesLayerSource,
  style: new Style({
    fill: new Fill({
      color: "rgba(255, 255, 255, 0)",
    }),
    stroke: new Stroke({
      color: "rgba(124, 252, 0, 0)",
      width: 2,
    }),
  }),
});

const municipalitiesLayer = new VectorLayer({
  zIndex: 2,
  name: "municipalLayer",
  source: municipalitiesLayerSource,
  style: new Style({
    fill: new Fill({
      color: "rgba(255, 255, 255, 0)",
    }),
    stroke: new Stroke({
      color: "rgba(0, 0, 0, 0)",
      width: 2,
    }),
  }),
});

var destinationIcon =
  '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
  '<g clip-path="url(#clip0_1973_5019)">\n' +
  '<circle cx="12" cy="7" r="4" fill="#00FFFFFF" opacity="0.1"/>\n' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M11.5776 0.0106317C11.0633 0.0393741 10.4957 0.134299 9.99844 0.274737C7.53578 0.97019 5.59679 2.91418 4.91051 5.37577C4.75299 5.94078 4.67173 6.46498 4.64474 7.09029C4.62842 7.46853 4.6481 7.7705 4.71482 8.16483C4.97992 9.73182 5.94286 12.067 7.56702 15.0816C8.40412 16.6354 9.44144 18.4075 10.5537 20.184C10.9596 20.8324 11.183 21.1791 11.2389 21.2473C11.3668 21.4037 11.5717 21.5278 11.784 21.5773C11.901 21.6046 12.1201 21.6022 12.2346 21.5723C12.419 21.5242 12.5784 21.4341 12.707 21.3055C12.7826 21.23 12.8588 21.1185 13.2048 20.5776C15.1249 17.5766 16.8246 14.5374 17.892 12.1968C18.7476 10.3205 19.2282 8.85171 19.3383 7.77603C19.3767 7.40023 19.3626 6.89599 19.3002 6.41294C19.0447 4.4365 17.9954 2.64631 16.395 1.45629C15.218 0.581006 13.8652 0.0923757 12.3901 0.0096525C12.1942 -0.0013395 11.7829 -0.000840297 11.5776 0.0106317ZM11.7456 3.51022C11.7298 3.51204 11.6693 3.51824 11.6112 3.524C11.0576 3.57893 10.4443 3.81091 9.98725 4.13827C9.17327 4.72125 8.66488 5.56274 8.52898 6.55203C8.50763 6.70752 8.49952 7.13925 8.51479 7.30768C8.57044 7.92127 8.79004 8.52085 9.14055 9.01615C9.36736 9.33667 9.6899 9.65602 10.0128 9.8798C10.4126 10.1569 10.8998 10.3575 11.3826 10.444C11.7739 10.514 12.2261 10.514 12.6174 10.444C13.0297 10.3701 13.4548 10.2091 13.8192 9.98866C14.2026 9.75676 14.5786 9.40785 14.8433 9.03843C15.2079 8.5296 15.4331 7.91937 15.4854 7.29826C15.5035 7.08325 15.4924 6.69677 15.4624 6.49923C15.362 5.83878 15.1053 5.26335 14.6873 4.76129C14.0891 4.04275 13.2411 3.60022 12.2976 3.51417C12.197 3.50499 11.8149 3.50226 11.7456 3.51022ZM7.09921 18.299C7.05169 18.3035 6.85297 18.3226 6.65761 18.3413C3.58056 18.6361 1.55729 19.1366 0.620991 19.8345C0.251189 20.1102 0.0589204 20.401 0.0102388 20.7583C-0.00405561 20.8632 -0.00222201 21.0796 0.0138388 21.1824C0.11365 21.8217 0.700392 22.3524 1.78081 22.7808C3.25758 23.3663 5.62853 23.7524 8.76481 23.9182C11.3115 24.0527 14.2849 24.0202 16.6656 23.8317C20.3159 23.5428 22.6637 22.9135 23.5635 21.9828C23.8075 21.7304 23.9568 21.4468 23.9863 21.1798C23.9899 21.1467 23.9967 21.1173 24.0013 21.1145C24.0059 21.1116 24.0096 21.038 24.0096 20.9509C24.0096 20.8638 24.0059 20.7948 24.0013 20.7976C23.9967 20.8005 23.9902 20.7758 23.9869 20.7428C23.9796 20.672 23.9452 20.5438 23.9114 20.4614C23.7679 20.1123 23.3779 19.7711 22.8144 19.5017C21.7517 18.9938 20.0536 18.6233 17.6496 18.375C16.7457 18.2816 16.5634 18.273 16.3996 18.3157C16.1036 18.3929 15.857 18.5994 15.7296 18.8769C15.5051 19.3657 15.72 19.9562 16.2088 20.194C16.3501 20.2627 16.4432 20.2815 16.7788 20.3088C17.6148 20.377 18.3647 20.4677 19.1616 20.5972C19.6603 20.6783 20.3285 20.806 20.3856 20.8312C20.4307 20.8511 20.4895 20.9131 20.5102 20.9626C20.5182 20.9818 20.5247 21.0241 20.5246 21.0564C20.5242 21.1526 20.4755 21.2348 20.3957 21.2734C20.3464 21.2973 20.0765 21.3492 19.4736 21.4509C17.6017 21.7666 15.8329 21.9383 13.8816 21.9937C13.4511 22.006 10.6589 22.006 10.2048 21.9937C8.17898 21.9392 6.27415 21.7494 4.32481 21.4079C3.63606 21.2872 3.58932 21.2756 3.53635 21.2129C3.47447 21.1396 3.456 21.0231 3.49347 20.9424C3.51533 20.8954 3.57407 20.8378 3.61921 20.8191C3.65536 20.8041 4.0116 20.7315 4.35361 20.6695C5.28876 20.4999 6.17857 20.3831 7.17121 20.2997C7.54394 20.2684 7.60147 20.2565 7.76055 20.178C8.1799 19.971 8.39906 19.4905 8.2816 19.0357C8.18588 18.6651 7.89004 18.3846 7.51201 18.3062C7.41815 18.2867 7.25546 18.2839 7.09921 18.299ZM0.00393159 20.9616C0.00392199 21.0514 0.00536199 21.0894 0.00713799 21.0461C0.00891399 21.0028 0.00892359 20.9294 0.00715719 20.8829C0.00540039 20.8365 0.00394119 20.8719 0.00393159 20.9616Z" fill="#00640099"/>\n' +
  "</g>\n" +
  "<defs>\n" +
  '<clipPath id="clip0_1973_5019">\n' +
  '<rect width="24" height="24" fill="white"/>\n' +
  "</clipPath>\n" +
  "</defs>\n" +
  "</svg>\n";

const DestinationsLayer = new VectorLayer({
  zIndex: 4,
  name: "destinationLayer",
  style: new Style({
    /* image: new Circle({
          radius: 10,
          fill: new Fill({color: 'rgba(0, 100, 0, 0.6)'}),
          stroke: new Stroke({color: 'red', width: 1}),
        }),*/
    image: new Icon({
      src:
        "data:image/svg+xml;charset=utf-8," +
        encodeURIComponent(destinationIcon),
    }),
  }),
  source: destinationLayerSource,
  visible: false,
});

const HiddenDestinationsLayer = new VectorLayer({
  zIndex: 20,
  name: "hidden",
  visible: false,
  source: hiddenDestinationLayerSource,
});

var siteIcon =
  '<svg  width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
  '<g clip-path="url(#clip0_106_372)">\n' +
  '<circle cx="12" cy="10" r="5" fill="#00FFFFFF" opacity="0.1"/>\n' +
  '<path d="M12 0C9.48126 0.00264713 7.06643 1.00439 5.28541 2.78541C3.50439 4.56643 2.50265 6.98126 2.5 9.5C2.5 16.05 9 22 11 23.65C11.2687 23.8763 11.6087 24.0005 11.96 24.0005C12.3113 24.0005 12.6513 23.8763 12.92 23.65C15 22 21.5 16.05 21.5 9.5C21.4974 6.98126 20.4956 4.56643 18.7146 2.78541C16.9336 1.00439 14.5187 0.00264713 12 0ZM12 14.5C11.0111 14.5 10.0444 14.2068 9.22215 13.6573C8.3999 13.1079 7.75904 12.327 7.3806 11.4134C7.00216 10.4998 6.90315 9.49445 7.09607 8.52455C7.289 7.55464 7.7652 6.66373 8.46447 5.96447C9.16373 5.2652 10.0546 4.789 11.0245 4.59607C11.9945 4.40315 12.9998 4.50216 13.9134 4.8806C14.827 5.25904 15.6079 5.8999 16.1573 6.72215C16.7068 7.54439 17 8.51109 17 9.5C17 10.1566 16.8707 10.8068 16.6194 11.4134C16.3681 12.02 15.9998 12.5712 15.5355 13.0355C15.0712 13.4998 14.52 13.8681 13.9134 14.1194C13.3068 14.3707 12.6566 14.5 12 14.5Z" fill="#0000ff99"/>\n' +
  "</g>\n" +
  "<defs>\n" +
  '<clipPath id="clip0_106_372">\n' +
  '<rect width="24" height="24" fill="black"/>\n' +
  "</clipPath>\n" +
  "</defs>\n" +
  "</svg>";

const SiteLayer = new VectorLayer({
  zIndex: 6,
  name: "siteLayer",
  style: new Style({
    /* image: new Circle({
          radius: 10,
          fill: new Fill({color: 'rgba(0, 0, 255, 0.6)'}),
          stroke: new Stroke({color: 'red', width: 1}),
        }),*/
    image: new Icon({
      src: "data:image/svg+xml;charset=utf-8," + encodeURIComponent(siteIcon),
    }),
  }),
  source: siteSource,
  visible: false,
});

const HiddenSiteLayer = new VectorLayer({
  name: "hidden",
  visible: false,
  source: hiddenSiteLayerSource,
});

const temporaryLayer = new VectorLayer({
  zIndex: 7,
  name: "temp",
  style: new Style({
    image: new Circle({
      radius: 10,
      fill: new Fill({ color: "rgba(254, 202, 58, 1)" }),
      stroke: new Stroke({ color: "yellow", width: 1 }),
    }),
  }),
  source: temporaryLayerSource,
});

const landUseUnitLayer = new VectorLayer({
  zIndex: 8,
  name: "landUseUnits",
  style: new Style({
    fill: new Fill({
      color: "rgba(255, 255, 255, 0)",
    }),
    stroke: new Stroke({
      color: "rgba(3, 179, 40, 0)",
      width: 2,
    }),
  }),
  source: landUseUnitSource,
});

const entitiesLayer = new VectorLayer({
  zIndex: 9,
  name: "entities",
  style: new Style({
    fill: new Fill({
      color: "rgba(255, 255, 255, 0)",
    }),
    stroke: new Stroke({
      color: "rgba(78, 88, 39, 0)",
      width: 2,
    }),
  }),
  source: entityLayerSource,
});

const parkDistrictLayer = new VectorLayer({
  zIndex: 10,
  name: "parkDistricts",
  style: new Style({
    fill: new Fill({
      color: "rgba(255, 255, 255, 0)",
    }),
    stroke: new Stroke({
      color: "rgba(228, 255, 0, 0)",
      width: 2,
    }),
  }),
  source: parkDistrictLayerSource,
});

const regionalUnitsLayer = new VectorLayer({
  zIndex: 11,
  name: "regionalUnits",
  style: new Style({
    fill: new Fill({
      color: "rgba(255, 255, 255, 0)",
    }),
    stroke: new Stroke({
      color: "rgba(0, 43, 255, 0)",
      width: 2,
    }),
  }),
  source: regionalUnitsLayerSource,
});

const layerResetControl = new Control({
  element: document.createElement("div"),
  target: document.getElementById("map-container"),
});

const layerSelectionControl = new Control({
  element: document.createElement("div"),
  target: document.getElementById("map-container"),
});

const selected = new Style({
  fill: new Fill({
    color: "rgba(251, 246, 8, 0.5)",
  }),
  stroke: new Stroke({
    color: "rgba(255, 255, 255, 0.7)",
    width: 2,
  }),
  zIndex: 60000,
});

const selectedPoint = new Style({
  image: new Circle({
    radius: 10,
    fill: new Fill({ color: "rgba(254, 202, 58, 1)" }),
    stroke: new Stroke({ color: "yellow", width: 1 }),
  }),
  zIndex: 60000,
});

function selectStyle(feature) {
  if (feature.getGeometry().getType() === "Point") {
    return selectedPoint;
  } else {
    return selected;
  }
}

let infoOverlay;
let infoOverlayElement;

class MapViewOl extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      mapSelection: "taustakartta",
      addPlaceMode: false,
      codeClassesLoaded: false,
      codeClasses: [],
      mapLoaded: false,
      layersLoaded: false,
      loadingMMLAreas: false,
      loadingMMLBoundaries: false,
      currentLayerPointCount: false,
      mapBoundariesLoadPercentCompleted: 0,
      urlSearchParams: {},
      viewBackgroundMaps: false,
      lastAdderess: "",
      cursorAddMode: false,
    };

    this.handleBaseMapChange = this.handleBaseMapChange.bind(this);
    this.exportData = this.exportData.bind(this);
    this.addPlace = this.addPlace.bind(this);
    this.handleMapClick = this.handleMapClick.bind(this);
    this.swapMapManager = this.swapMapManager.bind(this);
    this.resetLayers = this.resetLayers.bind(this);
    this.loadDestinations = this.loadDestinations.bind(this);
    this.clearMapSelection = this.clearMapSelection.bind(this);
    this.updateDestinationLayerWithFilters =
      this.updateDestinationLayerWithFilters.bind(this);
    this.loadSites = this.loadSites.bind(this);
    this.updateSites = this.updateSites.bind(this);
    this.updateDestinations = this.updateDestinations.bind(this);
    this.updateSiteLayerWithFilters =
      this.updateSiteLayerWithFilters.bind(this);
    this.adjustLayersToCurrentPath = this.adjustLayersToCurrentPath.bind(this);
    this.handleRightClick = this.handleRightClick.bind(this);
    this.checkIsLayerActive = this.checkIsLayerActive.bind(this);
    this.centerAndZoomOutMap = this.centerAndZoomOutMap.bind(this);
  }

  checkIsLayerActive(name) {
    switch (name) {
      case "countyLayer":
        return this.props.layers.countiesLayerVisibility;
      case "destinationLayer":
        return this.props.layers.destinationLayerVisibility;
      case "entities":
        return this.props.layers.entitiesLayerVisibility;
      case "landUseUnits":
        return (
          this.props.layers.landUseUnitLayerFillVisibility ||
          this.props.landUseUnitLayerStrokeVisibility
        );
      case "municipalLayer":
        return this.props.layers.municipalitiesLayerVisibility;
      case "parkDistricts":
        return this.props.layers.parkDistrictsLayerVisibility;
      case "regionalUnits":
        return this.props.layers.regionalUnitsLayerVisibility;
      case "siteLayer":
        return this.props.layers.siteLayerVisibility;
      default:
        return false;
    }
  }

  CreateInfoOverlay(resultList) {
    let siteData =
      typeof resultList["siteLayer"] !== "undefined"
        ? `<p style="margin-bottom:1px">${this.props.t(
            "overlay_site_title"
          )}: ${resultList["siteLayer"]}</p>`
        : "";
    let destinationData =
      typeof resultList["destinationLayer"] !== "undefined"
        ? `<p style="margin-bottom:1px">${this.props.t(
            "overlay_destination_title"
          )}: ${resultList["destinationLayer"]}</p>`
        : "";
    let countyData =
      typeof resultList["countyLayer"] !== "undefined"
        ? `<p style="margin-bottom:1px">${this.props.t(
            "overlay_counties_title"
          )}: ${resultList["countyLayer"]}</p>`
        : "";
    let municipalData =
      typeof resultList["municipalLayer"] !== "undefined"
        ? `<p style="margin-bottom:1px">${this.props.t(
            "overlay_municipalities_title"
          )} : ${resultList["municipalLayer"]}</p>`
        : "";
    let regionalUnitsData =
      typeof resultList["regionalUnits"] !== "undefined"
        ? `<p style="margin-bottom:1px">${this.props.t(
            "overlay_regionalUnits_title"
          )}: ${resultList["regionalUnits"]}</p>`
        : "";
    let parkDistrictsData =
      typeof resultList["parkDistricts"] !== "undefined"
        ? `<p style="margin-bottom:1px">${this.props.t(
            "overlay_parkDistrict_title"
          )}: ${resultList["parkDistricts"]}</p>`
        : "";
    let entitiesData =
      typeof resultList["entities"] !== "undefined"
        ? `<p style="margin-bottom:1px">${this.props.t(
            "overlay_entities_title"
          )}: ${resultList["entities"]}</p>`
        : "";
    let landUseUnitsData =
      typeof resultList["landUseUnit"] !== "undefined"
        ? `<p style="margin-bottom:1px">${this.props.t(
            "overlay_landUseUnit_title"
          )}: ${resultList["landUseUnit"]}</p>`
        : "";
    let landUseUnitCodeData =
      typeof resultList["landUseUnitCode"] !== "undefined"
        ? `<p style="margin-bottom:1px">${this.props.t(
            "overlay_landUseUnitType_title"
          )}: ${resultList["landUseUnitCode"]}</p>`
        : "";

    return `
      <div style="display:flex;justify-content:space-between">
      <p style="font-weight:bold">${this.props.t("overlay_title")}</p>
      <div style="text-align:end"><img src="${close_fill}" id='overlay-close-button'/></div></div>
          <div>
            ${siteData}
            ${destinationData}
            ${regionalUnitsData}
            ${parkDistrictsData}
            ${entitiesData}
            ${landUseUnitsData}
            ${landUseUnitCodeData}
            ${countyData}
            ${municipalData}
          </div>`;
  }

  swapMapManager() {
    let status = !this.props.layers.showMapManager;
    if (status === true) {
      var element = document.getElementById("layerManagerMapButton");
      element.parentNode.removeChild(element);
    } else {
      if (document.getElementById("layerManagerMapButton") === null) {
        this.createLayerSwapperButton();
      }
    }
    this.props.dispatch(mapLayerSelector.changeShowMapManager());
  }

  resetLayers() {
    if (this.props.location.pathname === "/") {
      this.deSelectLayers();
      if (
        this.props.storeHelper.getState().layerSelector.layers
          .destinationLayerVisibility
      ) {
        this.props.dispatch(
          mapLayerSelector.changeDestinationLayerVisibility()
        );
        DestinationsLayer.setVisible(
          this.props.storeHelper.getState().layerSelector.layers
            .destinationLayerVisibility
        );
      }
      if (
        this.props.storeHelper.getState().layerSelector.layers
          .siteLayerVisibility
      ) {
        this.props.dispatch(mapLayerSelector.changeSiteLayerVisibility());
        SiteLayer.setVisible(
          this.props.storeHelper.getState().layerSelector.layers
            .siteLayerVisibility
        );
      }
      this.handleBaseMapChange("taustakartta");
    }
    if (this.props.location.pathname.startsWith("/destination")) {
      if (
        this.props.storeHelper.getState().layerSelector.layers
          .siteLayerVisibility
      ) {
        this.props.dispatch(mapLayerSelector.changeSiteLayerVisibility());
        SiteLayer.setVisible(
          this.props.storeHelper.getState().layerSelector.layers
            .siteLayerVisibility
        );
      }
      if (!this.props.destinationFilters.destinationFunder.selectAll)
        this.props.dispatch(
          destinationsFilterSelector.changeDestinationFunderCodeSelectAll()
        );
      if (!this.props.destinationFilters.destinationLandUseUnitFilter.selectAll)
        this.props.dispatch(
          destinationsFilterSelector.changeDestinationLandUseUnitCodeSelectAll()
        );
      if (!this.props.destinationFilters.destinationOwner.selectAll)
        this.props.dispatch(
          destinationsFilterSelector.changeDestinationOwnerCodeSelectAll()
        );
      if (!this.props.destinationFilters.destinationType.selectAll)
        this.props.dispatch(
          destinationsFilterSelector.changeDestinationTypeSelectAll()
        );
      if (this.props.destinationFilters.destination.notInUse)
        this.props.dispatch(
          destinationsFilterSelector.changeDestinationSelection("notInUse")
        );
      if (!this.props.destinationFilters.destination.inUse)
        this.props.dispatch(
          destinationsFilterSelector.changeDestinationSelection("inUse")
        );
    }
    if (this.props.location.pathname.startsWith("/site")) {
      if (
        this.props.storeHelper.getState().layerSelector.layers
          .destinationLayerVisibility
      ) {
        this.props.dispatch(
          mapLayerSelector.changeDestinationLayerVisibility()
        );
        DestinationsLayer.setVisible(
          this.props.storeHelper.getState().layerSelector.layers
            .destinationLayerVisibility
        );
      }
      if (this.props.siteFilters.site.notSpecified)
        this.props.dispatch(
          sitesFilterSelector.changeSiteSelection("notSpecified")
        );
      if (this.props.siteFilters.site.planned)
        this.props.dispatch(sitesFilterSelector.changeSiteSelection("planned"));
      if (this.props.siteFilters.site.underConstruction)
        this.props.dispatch(
          sitesFilterSelector.changeSiteSelection("underConstruction")
        );
      if (!this.props.siteFilters.site.inUse)
        this.props.dispatch(sitesFilterSelector.changeSiteSelection("inUse"));
      if (this.props.siteFilters.site.discarded)
        this.props.dispatch(
          sitesFilterSelector.changeSiteSelection("discarded")
        );
    }

    this.clearMapSelection();
  }

  deSelectLayers() {
    let layers = [
      "counties",
      "municipalities",
      "regionalUnits",
      "parkDistricts",
      "entities",
      "landUseUnitsStroke",
      "landUseUnitsFill",
    ];
    layers.forEach((layer) => {
      switch (layer) {
        case "counties":
          if (this.props.layers.countiesLayerVisibility)
            this.props.dispatch(
              mapLayerSelector.changeCountiesLayerVisibility()
            );
          let cStyle = countiesLayer.getStyle();
          cStyle.getStroke().setColor([124, 252, 0, 0]);
          countiesLayer.setStyle(cStyle);
          break;
        case "municipalities":
          if (this.props.layers.municipalitiesLayerVisibility)
            this.props.dispatch(
              mapLayerSelector.changeMunicipalitiesLayerVisibility()
            );
          let mStyle = municipalitiesLayer.getStyle();
          mStyle.getStroke().setColor([0, 0, 0, 0]);
          municipalitiesLayer.setStyle(mStyle);
          break;
        case "regionalUnits":
          if (this.props.layers.regionalUnitsLayerVisibility)
            this.props.dispatch(
              mapLayerSelector.changeRegionalUnitsLayerVisibility()
            );
          let regStyle = regionalUnitsLayer.getStyle();
          regStyle.getStroke().setColor([0, 43, 255, 0]);
          regionalUnitsLayer.setStyle(regStyle);
          break;
        case "parkDistricts":
          if (this.props.layers.parkDistrictsLayerVisibility)
            this.props.dispatch(
              mapLayerSelector.changeParkDistrictsLayerVisibility()
            );
          let parkStyle = parkDistrictLayer.getStyle();
          parkStyle.getStroke().setColor([78, 88, 39, 0]);
          parkDistrictLayer.setStyle(parkStyle);
          break;
        case "entities":
          if (this.props.layers.entitiesLayerVisibility)
            this.props.dispatch(
              mapLayerSelector.changeEntitiesLayerVisibility()
            );
          let entityStyle = entitiesLayer.getStyle();
          entityStyle.getStroke().setColor([228, 255, 0, 0]);
          entitiesLayer.setStyle(entityStyle);
          break;
        case "landUseUnitsStroke":
          if (this.props.layers.landUseUnitLayerStrokeVisibility)
            this.props.dispatch(
              mapLayerSelector.changeLandUseUnitLayerStrokeVisibility()
            );
          let landUseUnitStrokeStyle = landUseUnitLayer.getStyle();
          landUseUnitStrokeStyle.getStroke().setColor([3, 179, 40, 0]);
          landUseUnitLayer.setStyle(landUseUnitStrokeStyle);
          break;
        case "landUseUnitsFill":
          if (this.props.layers.landUseUnitLayerFillVisibility)
            this.props.dispatch(
              mapLayerSelector.changeLandUseUnitLayerFillVisibility()
            );
          let landUseUnitFillStyle = landUseUnitLayer.getStyle();
          landUseUnitFillStyle.getFill().setColor([3, 179, 40, 0]);
          landUseUnitLayer.setStyle(landUseUnitFillStyle);
          break;
        default:
          break;
      }
    });
  }

  selectSingleClick = new Select({
    style: selectStyle,
    features: mapFeatureSelection,
    condition: (mapBrowserEvent) => {
      return this.state.addPlaceMode === false && singleClick(mapBrowserEvent);
    },
    addCondition: (mapBrowserEvent) => {
      return this.state.addPlaceMode === false && singleClick(mapBrowserEvent);
    },
    toggleCondition: (mapBrowserEvent) => {
      return platformModifierKeyOnly(mapBrowserEvent);
    },
    removeCondition: (mapBrowserEvent) => {
      return singleClick(mapBrowserEvent);
    },
    layers: (layer) => {
      return this.checkIsLayerActive(layer.values_.name);
    },
  });

  baseMap = new Map({
    controls: [
      mousePositionControl,
      new Zoom({
        className: "zoom-placement",
        zoomInTipLabel: this.props.t("zoom_in"),
        zoomInClassName: "btn btn-primary zoom-button-width",
        zoomOutTipLabel: this.props.t("zoom_out"),
        zoomOutClassName: "btn btn-primary zoom-button-width",
      }),
      new ScaleLine({
        units: "metric",
        bar: false,
      }),
      layerResetControl,
      layerSelectionControl,
    ],
    renderer: "webgl",
    layers: [
      DestinationsLayer,
      SiteLayer,
      regionalUnitsLayer,
      parkDistrictLayer,
      entitiesLayer,
      landUseUnitLayer,
      countiesLayer,
      municipalitiesLayer,
      temporaryLayer,
      HiddenDestinationsLayer,
      HiddenSiteLayer,
    ],
    view: new View({
      center: [479501.6411, 7091190.5957],
      projection: "EPSG:3067",
      zoom: 3,
      minZoom: 2,
      maxZoom: 12,
    }),
  });

  setBaseMapLayer() {
    let layer = new TileLayer({
      zIndex: 1,
      source: new XYZ({
        attributions: [
          '&copy; <a href="https://www.maanmittauslaitos.fi/avoindata-lisenssi-cc40">Maanmittauslaitos</a> contributors',
        ],
        url:
          "https://maps-mml.anderscloud.com/wmts/?ngsw-bypass=1&layer=" +
          this.props.mapCommons.map.mapSelection +
          "&style=default&tilematrixset=ETRS-TM35FIN&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fpng&TileMatrix={z}&TileCol={x}&TileRow={y}",
        projection: "EPSG:3067",
        tileGrid: new TileGrid({
          extent: extent,
          resolutions: resolutions,
          tileSize: [256, 256],
        }),
      }),
    });
    layer.set("name", baseMapLayerName);
    return layer;
  }

  handleBaseMapChange(value) {
    this.props.dispatch(mapCommonsSelector.changeBasemap(value));
    if (typeof this.baseMap !== "undefined") {
      this.baseMap.getLayers().forEach((layer) => {
        if (typeof layer !== "undefined") {
          if (layer.get("name") && layer.get("name") === baseMapLayerName) {
            layer
              .getSource()
              .setUrl(
                "https://maps-mml.anderscloud.com/wmts/?ngsw-bypass=1&layer=" +
                  value +
                  "&style=default&tilematrixset=ETRS-TM35FIN&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fpng&TileMatrix={z}&TileCol={x}&TileRow={y}"
              );
          }
        }
      });
      this.setState((state) => ({ ...state, mapSelection: value }));
    }
  }

  handleRightClick(event) {
    let selection = this.baseMap.getFeaturesAtPixel(
      this.baseMap.getEventPixel(event.originalEvent)
    );
    let resultlist = {};

    if (selection.length > 0) {
      this.baseMap.forEachFeatureAtPixel(
        this.baseMap.getEventPixel(event.originalEvent),
        (feature, layer) => {
          switch (layer.values_.name) {
            case "siteLayer":
              if (this.props.layers.siteLayerVisibility) {
                let siteId = feature.values_.siteId;
                let siteName = feature.values_.siteName;

                if (
                  typeof siteId !== "undefined" &&
                  typeof siteName !== "undefined"
                ) {
                  let resultText = siteId + " " + siteName;
                  resultlist["siteLayer"] = resultText;
                }
              }
              break;
            case "destinationLayer":
              if (this.props.layers.destinationLayerVisibility) {
                let destinationId = feature.values_.destinationId;
                let destinationName = feature.values_.destinationName;

                if (
                  typeof destinationId !== "undefined" &&
                  typeof destinationName !== "undefined"
                ) {
                  let resultText = destinationId + " " + destinationName;
                  resultlist["destinationLayer"] = resultText;
                }
              }
              break;
            case "countyLayer":
              if (this.props.layers.countiesLayerVisibility) {
                let countyCodeId = feature.values_.CountyId;
                let countyClassId = feature.values_.CountyTypeCodeClassId;

                if (
                  typeof countyCodeId !== "undefined" &&
                  typeof countyClassId !== "undefined"
                ) {
                  let resultText =
                    countyCodeId +
                    " " +
                    getCodeText(
                      countyClassId,
                      countyCodeId,
                      this.props.t("common:GMLLanguage"),
                      this.props.storeHelper.getState().codesSelector.codes
                        .codeClasses
                    );
                  resultlist["countyLayer"] = resultText;
                }
              }
              break;
            case "municipalLayer":
              if (this.props.layers.municipalitiesLayerVisibility) {
                let municipalityId = feature.values_.MunicipalityId;
                let municipalityClassId =
                  feature.values_.MunicipalityTypeCodeClassId;

                if (
                  typeof municipalityId !== "undefined" &&
                  typeof municipalityClassId !== "undefined"
                ) {
                  let resultText =
                    municipalityId +
                    " " +
                    getCodeText(
                      municipalityClassId,
                      municipalityId,
                      this.props.t("common:GMLLanguage"),
                      this.props.storeHelper.getState().codesSelector.codes
                        .codeClasses
                    );
                  resultlist["municipalLayer"] = resultText;
                }
              }
              break;
            case "regionalUnits":
              if (this.props.layers.regionalUnitsLayerVisibility) {
                let regionalUnitCodeId = feature.values_.RegionalUnitTypeCodeId;
                let regionalUnitClassId =
                  feature.values_.RegionalUnitTypeCodeClassId;

                if (
                  typeof regionalUnitCodeId !== "undefined" &&
                  typeof regionalUnitClassId !== "undefined"
                ) {
                  let resultText =
                    regionalUnitCodeId +
                    " " +
                    getCodeText(
                      regionalUnitClassId,
                      regionalUnitCodeId,
                      this.props.t("common:GMLLanguage"),
                      this.props.storeHelper.getState().codesSelector.codes
                        .codeClasses
                    );
                  resultlist["regionalUnits"] = resultText;
                }
              }
              break;
            case "parkDistricts":
              if (this.props.layers.parkDistrictsLayerVisibility) {
                let parkDistrictCodeId = feature.values_.ParkDistrictTypeCodeId;
                let parkDistrictClassId =
                  feature.values_.ParkDistrictTypeCodeClassId;

                if (
                  typeof parkDistrictCodeId !== "undefined" &&
                  typeof parkDistrictClassId !== "undefined"
                ) {
                  let resultText =
                    parkDistrictCodeId +
                    " " +
                    getCodeText(
                      parkDistrictClassId,
                      parkDistrictCodeId,
                      this.props.t("common:GMLLanguage"),
                      this.props.storeHelper.getState().codesSelector.codes
                        .codeClasses
                    );
                  resultlist["parkDistricts"] = resultText;
                }
              }
              break;
            case "entities":
              if (this.props.layers.entitiesLayerVisibility) {
                let entityCodeId = feature.values_.EntityTypeCodeId;
                let entityClassId = feature.values_.EntityTypeCodeClassId;

                if (
                  typeof entityCodeId !== "undefined" &&
                  typeof entityClassId !== "undefined"
                ) {
                  let resultText =
                    entityCodeId +
                    " " +
                    getCodeText(
                      entityClassId,
                      entityCodeId,
                      this.props.t("common:GMLLanguage"),
                      this.props.storeHelper.getState().codesSelector.codes
                        .codeClasses
                    );
                  resultlist["entities"] = resultText;
                }
              }
              break;
            case "landUseUnits":
              if (
                this.props.layers.landUseUnitLayerFillVisibility ||
                this.props.layers.landUseUnitLayerStrokeVisibility
              ) {
                let landUseUnitId = feature.values_.LandUseUnitId;
                let landUseUnitClassId =
                  feature.values_.LandUseUnitTypeCodeClassId;
                let landUseUnitTypeId = feature.values_.LandUseUnitTypeCodeId;
                let landUseUnitName = feature.values_.LandUseUnitName;

                if (
                  typeof landUseUnitId !== "undefined" &&
                  typeof landUseUnitClassId !== "undefined"
                ) {
                  let resultUnitText = landUseUnitId + " " + landUseUnitName;
                  let resultCodeText =
                    landUseUnitTypeId +
                    " " +
                    getCodeText(
                      landUseUnitClassId,
                      landUseUnitTypeId,
                      this.props.t("common:GMLLanguage"),
                      this.props.storeHelper.getState().codesSelector.codes
                        .codeClasses
                    );
                  resultlist["landUseUnit"] = resultUnitText;
                  resultlist["landUseUnitCode"] = resultCodeText;
                }
              }
              break;
            default:
              break;
          }
        }
      );

      if (Object.keys(resultlist).length === 0) {
        event.preventDefault();
        return;
      }

      if (infoOverlayElement) {
        infoOverlayElement.parentNode.removeChild(infoOverlayElement);
      }

      const coordinate = event.coordinate;

      infoOverlayElement = document.createElement("div");
      infoOverlayElement.className = "info-overlay";
      infoOverlayElement.innerHTML = this.CreateInfoOverlay(resultlist);
      infoOverlayElement
        .querySelector("#overlay-close-button")
        .addEventListener("click", () => {
          infoOverlay.setPosition(undefined);
        });
      infoOverlay = new Overlay({
        element: infoOverlayElement,
        positioning: "bottom-center",
        position: coordinate,
        autopan: true,
      });
      this.baseMap.addOverlay(infoOverlay);
    }

    event.preventDefault();
  }

  handleMapClick(event) {
    if (typeof this.baseMap !== "undefined" && this.state.cursorAddMode) {
      this.setState({
        addPlaceMode: !this.state.addPlaceMode,
        cursorAddMode: false,
      });
      this.baseMap.getViewport().className = this.baseMap
        .getViewport()
        .className.replace("custom-cursor", "");
      let selectedD = null;

      let selection = this.baseMap.getFeaturesAtPixel(
        this.baseMap.getEventPixel(event.originalEvent)
      );

      if (selection.length > 0) {
        let placement = event.coordinate;
        let tempFeature = new Feature({
          geometry: new Point(placement),
        });
        temporaryLayerSource.addFeature(tempFeature);

        let countyCodeId = -1;
        let municipalityCodeId = -1;
        let regionalUnitCodeId = -1;
        let parkDistrictCodeId = -1;
        let entityCodeId = -1;
        let landUseUnitTypeId = -1;
        let landUseUnitId = -1;
        let landUseUnitName = "";

        this.baseMap.forEachFeatureAtPixel(
          this.baseMap.getEventPixel(event.originalEvent),
          (feature, layer) => {
            switch (layer.values_.name) {
              case "countyLayer":
                countyCodeId = feature.values_.CountyId;
                break;
              case "municipalLayer":
                municipalityCodeId = feature.values_.MunicipalityId;
                break;
              case "regionalUnits":
                regionalUnitCodeId = feature.values_.RegionalUnitTypeCodeId;
                break;
              case "parkDistricts":
                parkDistrictCodeId = feature.values_.ParkDistrictTypeCodeId;
                break;
              case "entities":
                entityCodeId = feature.values_.EntityTypeCodeId;
                break;
              case "landUseUnits":
                landUseUnitTypeId = feature.values_.LandUseUnitTypeCodeId;
                landUseUnitId = feature.values_.LandUseUnitId;
                landUseUnitName = feature.values_.LandUseUnitName;
                break;
              default:
                console.log("No handler for " + layer.values_.name);
                break;
            }
          }
        );

        let latLon = proj4(proj4("EPSG:3067"), proj4("EPSG:4326"), [
          parseFloat(event.coordinate[0]),
          parseFloat(event.coordinate[1]),
        ]);

        this.exportData({
          munCodeId: municipalityCodeId,
          cCodeId: countyCodeId,
          coordinate: event.coordinate,
          latLon: latLon,
          regionalUnitTypeCodeId: regionalUnitCodeId,
          parkDistrictTypeCodeId: parkDistrictCodeId,
          entityTypeCodeId: entityCodeId,
          landUseUnitTypeCodeId: landUseUnitTypeId,
          landUseUnitId: landUseUnitId,
          landUseUnitName: landUseUnitName,
          viewMode: 2,
          loadDestination: this.loadDestinations,
          selectedDestination: selectedD,
          updateSites: this.updateSites,
          updateDestinations: this.updateDestinations,
          loadSites: this.loadSites,
          clearMapSelection: this.clearMapSelection,
          centerMap: this.centerAndZoomOutMap,
        });
      }
    } else if (
      typeof this.baseMap !== "undefined" &&
      this.state.cursorAddMode === false
    ) {
      this.baseMap.getViewport().className = this.baseMap
        .getViewport()
        .className.replace("custom-cursor", "");

      let sel = this.baseMap.getFeaturesAtPixel(event.pixel);
      if (typeof sel !== "undefined" && sel.length > 0) {
        let point = sel.find((f) => f.getGeometry().getType() === "Point");

        if (
          typeof point !== "undefined" &&
          this.props.layers.countiesLayerVisibility === false &&
          this.props.layers.entitiesLayerVisibility === false &&
          this.props.layers.landUseUnitLayerFillVisibility === false &&
          this.props.layers.landUseUnitLayerStrokeVisibility === false &&
          this.props.layers.municipalitiesLayerVisibility === false &&
          this.props.layers.parkDistrictsLayerVisibility === false &&
          this.props.layers.regionalUnitsLayerVisibility === false
        ) {
          let removePointSelection = false;
          this.selectSingleClick.getFeatures().forEach((fe) => {
            if (fe.getId() === point.getId()) {
              removePointSelection = true;
            }
          });

          if (removePointSelection === true) {
            this.exportData({
              ...point.values_,
              visibility: false,
              loadSites: this.loadSites,
              loadDestination: this.loadDestinations,
              clearMapSelection: this.clearMapSelection,
              centerMap: this.centerAndZoomOutMap,
            });
          } else {
            this.exportData({
              ...point.values_,
              loadSites: this.loadSites,
              loadDestination: this.loadDestinations,
              clearMapSelection: this.clearMapSelection,
              centerMap: this.centerAndZoomOutMap,
            });
          }
        }
      }
    }
  }

  exportData(data) {
    this.props.onExportData(data);
  }

  clearMapSelection() {
    temporaryLayerSource.clear();
    this.selectSingleClick.getFeatures().clear();
    this.props.dispatch(
      mapSelectionSelector.updateSitesSelection({ list: [], listView: false })
    );
    this.props.dispatch(
      mapSelectionSelector.updateDestinationSelection({
        list: [],
        listView: false,
      })
    );
    this.updateDestinationLayerWithFilters();
    this.updateSiteLayerWithFilters();
  }

  async updateDestinations(destinationId) {
    let destinationResponse = await GetUserCreatedDestination(destinationId);

    if (destinationResponse instanceof AxiosError) {
      temporaryLayerSource.clear();
      console.error("Failed to gather counties: " + destinationResponse);
    } else {
      let geoJsonFormat = new GeoJSON();

      if (destinationResponse.data.length > 0) {
        hiddenDestinationLayerSource.addFeatures(
          destinationResponse.data.map((x) => geoJsonFormat.readFeature(x))
        );
        temporaryLayerSource.clear();
        this.updateDestinationLayerWithFilters();
      }
    }
  }

  async loadDestinations() {
    let countBeforeLoad = hiddenDestinationLayerSource.getFeatures().length;

    let destinationsResponse = await GetUserCreatedDestinations();

    if (destinationsResponse instanceof AxiosError) {
      console.error("Failed to gather destinations: " + destinationsResponse);
    } else {
      let geoJsonFormat = new GeoJSON();
      let elements = [];
      hiddenDestinationLayerSource.clear();
      if (destinationsResponse.data.length > 0) {
        hiddenDestinationLayerSource.addFeatures(
          destinationsResponse.data.map((x) => geoJsonFormat.readFeature(x))
        );

        let destLen = hiddenDestinationLayerSource.getFeatures().length;
        let tempLen = temporaryLayerSource.getFeatures().length;

        if (tempLen === 1 && destLen === countBeforeLoad + tempLen) {
          temporaryLayerSource.clear();
        }

        this.updateDestinationLayerWithFilters();
      }
    }
  }

  async updateSites(siteId) {
    let siteResponse = await GetUserCreatedSite(siteId);

    if (siteResponse instanceof AxiosError) {
      temporaryLayerSource.clear();
      console.error("Failed to gather counties: " + siteResponse);
    } else {
      let geoJsonFormat = new GeoJSON();
      hiddenSiteLayerSource.clear();
      if (siteResponse.data.length > 0) {
        hiddenSiteLayerSource.addFeatures(
          siteResponse.data.map((x) => geoJsonFormat.readFeature(x))
        );
        temporaryLayerSource.clear();
        this.updateSiteLayerWithFilters();
      }
    }
  }

  centerAndZoomOutMap() {
    this.baseMap.getView().setCenter([479501.6411, 7091190.5957]);
    this.baseMap.getView().setZoom(3);
  }

  async loadSites() {
    let countBeforeLoad = hiddenSiteLayerSource.getFeatures().length;

    let sitesResponse = await GetUserCreatedSites();

    if (sitesResponse instanceof AxiosError) {
      console.error("Failed to gather counties: " + sitesResponse);
    } else {
      let geoJsonFormat = new GeoJSON();
      let elements = [];
      hiddenSiteLayerSource.clear();

      if (sitesResponse.data.length > 0) {
        hiddenSiteLayerSource.addFeatures(
          sitesResponse.data.map((x) => geoJsonFormat.readFeature(x))
        );

        let siteLen = hiddenSiteLayerSource.getFeatures().length;
        let tempLen = temporaryLayerSource.getFeatures().length;
        if (tempLen === 1 && siteLen === countBeforeLoad + tempLen) {
          temporaryLayerSource.clear();
        }
      }
    }
    this.updateSiteLayerWithFilters();
  }

  updateSiteLayerWithFilters() {
    let features = this.selectSingleClick.getFeatures();

    siteSource.clear();

    let feats = [];
    let states = [
      "notSpecified",
      "planned",
      "underConstruction",
      "inUse",
      "discarded",
    ];
    for (let i = 0; i < states.length; i++) {
      if (
        this.props.storeHelper.getState().sitesFilterSelector.site[states[i]] &&
        features.getLength() === 0
      ) {
        feats = [
          ...feats,
          ...hiddenSiteLayerSource
            .getFeatures()
            .filter((feature) => parseInt(feature.get("statusCodeId")) === i),
        ];
        if (
          this.props.storeHelper.getState().mapSelectionSelector.selections
            .fromList
        ) {
          let listFilteredSites = [];
          feats.forEach((feature) => {
            if (
              this.props.storeHelper
                .getState()
                .mapSelectionSelector.selections.selectedSites.includes(
                  feature.id_
                )
            ) {
              listFilteredSites.push(feature);
            }
          });
          listFilteredSites.length > 0 &&
            siteSource.addFeatures(listFilteredSites);
          //keep it showing that selection is from list
          listFilteredSites.length > 0 &&
            this.props.dispatch(
              mapSelectionSelector.updateSitesSelection({
                list: listFilteredSites.map((m) => m.values_.siteId),
                listView: true,
              })
            );
        } else {
          feats.length > 0 && siteSource.addFeatures(feats);
          this.props.dispatch(
            mapSelectionSelector.updateSitesSelection({
              list: feats.map((m) => m.values_.siteId),
              listView: false,
            })
          );
        }
      } else if (
        this.props.storeHelper.getState().sitesFilterSelector.site[states[i]] &&
        features.getLength() > 0
      ) {
        feats = [
          ...feats,
          ...hiddenSiteLayerSource
            .getFeatures()
            .filter((feature) => parseInt(feature.get("statusCodeId")) === i),
        ];
        let locationFiltered = [];
        feats.forEach((site) => {
          features.forEach((feat) => {
            if (
              feat
                .getGeometry()
                .intersectsCoordinate(site.getGeometry().getFirstCoordinate())
            ) {
              locationFiltered.push(site);
            }
          });
        });

        if (locationFiltered.length > 0) {
          if (
            this.props.storeHelper.getState().mapSelectionSelector.selections
              .selectedSites.length > 0 &&
            this.props.storeHelper.getState().mapSelectionSelector.selections
              .fromList
          ) {
            let listFilteredSites = [];
            locationFiltered.forEach((feature) => {
              if (
                this.props.storeHelper
                  .getState()
                  .mapSelectionSelector.selections.selectedSites.includes(
                    feature.id_
                  )
              ) {
                listFilteredSites.push(feature);
              }
            });
            listFilteredSites.length > 0 &&
              siteSource.addFeatures(listFilteredSites);
            //keep it showing that selection is from list
            listFilteredSites.length > 0 &&
              this.props.dispatch(
                mapSelectionSelector.updateSitesSelection({
                  list: listFilteredSites.map((m) => m.values_.siteId),
                  listView: true,
                })
              );
          } else {
            siteSource.addFeatures(locationFiltered);
            this.props.dispatch(
              mapSelectionSelector.updateSitesSelection({
                list: locationFiltered.map((m) => m.values_.siteId),
                listView: false,
              })
            );
          }
        }
      }
    }

    if (feats.length === 0) {
      this.props.dispatch(
        mapSelectionSelector.updateSitesSelection({
          list: feats.map((m) => m.values_.siteId),
          listView: false,
        })
      );
    }
  }

  checkLandUseUnitRelatedFilters(landUseUnitCodes, selectedCodes) {
    if (selectedCodes.includes(0) && landUseUnitCodes.length === 0) {
      return true;
    }
    return landUseUnitCodes
      .map((code) => parseInt(code, 10))
      .some((code) => selectedCodes.includes(code));
  }

  updateDestinationLayerWithFilters() {
    let features = this.selectSingleClick.getFeatures();
    destinationLayerSource.clear();

    let destinationTypeCodes = this.getCodeClassRelatedFilterIds(1001);
    let destinationOwnerCodes = this.getCodeClassRelatedFilterIds(1002);
    let destinationFunderCodes = this.getCodeClassRelatedFilterIds(1003);
    let destinationLandUseUnitCodes = this.getCodeClassRelatedFilterIds(1011);

    let selectedTypeCodes = [];
    let selectedOwnerCodes = [];
    let selectedFunderCodes = [];
    let selectedLandUseUnitCodes = [];

    if (destinationTypeCodes !== null) {
      selectedTypeCodes = destinationTypeCodes
        .filter(
          (i) =>
            this.props.storeHelper.getState().destinationsFilterSelector
              .destinationType["typeCode_" + i.id] === true
        )
        .map((i) => i.id);
    }

    if (destinationOwnerCodes !== null) {
      selectedFunderCodes = destinationFunderCodes
        .filter(
          (i) =>
            this.props.storeHelper.getState().destinationsFilterSelector
              .destinationFunder["funderCode_" + i.id] === true
        )
        .map((i) => i.id);
    }

    if (destinationFunderCodes !== null) {
      destinationFunderCodes.forEach((i) => {
        if (
          this.props.storeHelper.getState().destinationsFilterSelector
            .destinationFunder["funderCode_" + i.id] === true
        ) {
          selectedFunderCodes.push(i.id);
        }
      });
    }

    if (destinationLandUseUnitCodes !== null) {
      selectedLandUseUnitCodes = destinationLandUseUnitCodes
        .filter(
          (i) =>
            this.props.storeHelper.getState().destinationsFilterSelector
              .destinationLandUseUnitFilter["landUseUnitCode_" + i.id] === true
        )
        .map((i) => i.id);
    }

    let feats = [];

    if (
      this.props.storeHelper.getState().destinationsFilterSelector.destination
        .inUse
    ) {
      feats = [
        ...feats,
        ...hiddenDestinationLayerSource
          .getFeatures()
          .filter(
            (feature) =>
              feature.get("inUse") === true &&
              (selectedTypeCodes.length > 0
                ? selectedTypeCodes.includes(
                    feature.get("destinationTypeCodeId")
                  )
                : true) &&
              (selectedFunderCodes.length > 0
                ? selectedFunderCodes.includes(
                    feature.get("destinationFunderCodeId")
                  )
                : true) &&
              (selectedOwnerCodes.length > 0
                ? selectedOwnerCodes.includes(
                    feature.get("destinationOwnerCodeId")
                  )
                : true) &&
              (selectedLandUseUnitCodes.length > 0
                ? this.checkLandUseUnitRelatedFilters(
                    feature.get("landUseUnitTypeCodes"),
                    selectedLandUseUnitCodes
                  )
                : true)
          ),
      ];

      if (feats.length > 0) {
        if (features.getLength() === 0) {
          if (
            this.props.storeHelper.getState().mapSelectionSelector.selections
              .fromList
          ) {
            let listFilteredDestiantions = [];
            feats.forEach((feature) => {
              if (
                this.props.storeHelper
                  .getState()
                  .mapSelectionSelector.selections.selectedDestinations.includes(
                    feature.id_
                  )
              ) {
                listFilteredDestiantions.push(feature);
              }
            });
            listFilteredDestiantions.length > 0 &&
              destinationLayerSource.addFeatures(listFilteredDestiantions);

            //keep it showing that selection is from list
            listFilteredDestiantions.length > 0 &&
              this.props.dispatch(
                mapSelectionSelector.updateDestinationSelection({
                  list: listFilteredDestiantions.map(
                    (m) => m.values_.destinationId
                  ),
                  listView: true,
                })
              );
          } else {
            destinationLayerSource.addFeatures(feats);
            this.props.dispatch(
              mapSelectionSelector.updateDestinationSelection({
                list: feats.map((m) => m.values_.destinationId),
                listView: false,
              })
            );
          }
        } else {
          let locationFiltered = [];

          feats.forEach((destination) => {
            features.forEach((feat) => {
              if (
                feat
                  .getGeometry()
                  .intersectsCoordinate(
                    destination.getGeometry().getFirstCoordinate()
                  )
              ) {
                locationFiltered.push(destination);
              }
            });
          });

          if (locationFiltered.length > 0) {
            if (
              this.props.storeHelper.getState().mapSelectionSelector.selections
                .selectedDestinations.length > 0 &&
              this.props.storeHelper.getState().mapSelectionSelector.selections
                .fromList
            ) {
              let listFilteredDestinations = [];
              locationFiltered.forEach((feature) => {
                if (
                  this.props.storeHelper
                    .getState()
                    .mapSelectionSelector.selections.selectedDestinations.includes(
                      feature.id_
                    )
                ) {
                  listFilteredDestinations.push(feature);
                }
              });
              listFilteredDestinations.length > 0 &&
                destinationLayerSource.addFeatures(listFilteredDestinations);

              //keep it showing that selection is from list

              listFilteredDestinations.length > 0 &&
                this.props.dispatch(
                  mapSelectionSelector.updateDestinationSelection({
                    list: listFilteredDestinations.map(
                      (m) => m.values_.destinationId
                    ),
                    listView: true,
                  })
                );
            } else {
              destinationLayerSource.addFeatures(locationFiltered);

              this.props.dispatch(
                mapSelectionSelector.updateDestinationSelection({
                  list: locationFiltered.map((m) => m.values_.destinationId),
                  listView: false,
                })
              );
            }
          }
        }
      }
    }

    if (
      this.props.storeHelper.getState().destinationsFilterSelector.destination
        .notInUse
    ) {
      feats = [
        ...feats,
        ...hiddenDestinationLayerSource
          .getFeatures()
          .filter(
            (feature) =>
              feature.get("inUse") === false &&
              (selectedTypeCodes.length > 0
                ? selectedTypeCodes.includes(
                    feature.get("destinationTypeCodeId")
                  )
                : true) &&
              (selectedFunderCodes.length > 0
                ? selectedFunderCodes.includes(
                    feature.get("destinationFunderCodeId")
                  )
                : true) &&
              (selectedOwnerCodes.length > 0
                ? selectedOwnerCodes.includes(
                    feature.get("destinationOwnerCodeId")
                  )
                : true) &&
              (selectedLandUseUnitCodes.length > 0
                ? this.checkLandUseUnitRelatedFilters(
                    feature.get("landUseUnitTypeCodes"),
                    selectedLandUseUnitCodes
                  )
                : true)
          ),
      ];

      if (feats.length > 0) {
        if (features.getLength() === 0) {
          if (
            this.props.storeHelper.getState().mapSelectionSelector.selections
              .fromList
          ) {
            let listFilteredDestiantions = [];
            feats.forEach((feature) => {
              if (
                this.props.storeHelper
                  .getState()
                  .mapSelectionSelector.selections.selectedDestinations.includes(
                    feature.id_
                  )
              ) {
                listFilteredDestiantions.push(feature);
              }
            });
            listFilteredDestiantions.length > 0 &&
              destinationLayerSource.addFeatures(listFilteredDestiantions);
            //keep it showing that selection is from list
            listFilteredDestiantions.length > 0 &&
              this.props.dispatch(
                mapSelectionSelector.updateDestinationSelection({
                  list: listFilteredDestiantions.map(
                    (m) => m.values_.destinationId
                  ),
                  listView: true,
                })
              );
          } else {
            destinationLayerSource.addFeatures(feats);
            this.props.dispatch(
              mapSelectionSelector.updateDestinationSelection({
                list: feats.map((m) => m.values_.destinationId),
                listView: false,
              })
            );
          }
        } else {
          let locationFiltered = [];

          feats.forEach((destination) => {
            features.forEach((feat) => {
              if (
                feat
                  .getGeometry()
                  .intersectsCoordinate(
                    destination.getGeometry().getFirstCoordinate()
                  )
              ) {
                locationFiltered.push(destination);
              }
            });
          });

          if (locationFiltered.length > 0) {
            if (
              this.props.storeHelper.getState().mapSelectionSelector.selections
                .selectedDestinations.length > 0 &&
              this.props.storeHelper.getState().mapSelectionSelector.selections
                .fromList
            ) {
              let listFilteredDestinations = [];
              locationFiltered.forEach((feature) => {
                if (
                  this.props.storeHelper
                    .getState()
                    .mapSelectionSelector.selections.selectedDestinations.includes(
                      feature.id_
                    )
                ) {
                  listFilteredDestinations.push(feature);
                }
              });
              listFilteredDestinations.length > 0 &&
                destinationLayerSource.addFeatures(listFilteredDestinations);
              //keep it showing that selection is from list
              listFilteredDestinations.length > 0 &&
                this.props.dispatch(
                  mapSelectionSelector.updateDestinationSelection({
                    list: listFilteredDestinations.map(
                      (m) => m.values_.destinationId
                    ),
                    listView: true,
                  })
                );
            } else {
              destinationLayerSource.addFeatures(locationFiltered);
              this.props.dispatch(
                mapSelectionSelector.updateDestinationSelection({
                  list: locationFiltered.map((m) => m.values_.destinationId),
                  listView: false,
                })
              );
            }
          }
        }
      }
    }
    if (feats.length === 0) {
      this.props.dispatch(
        mapSelectionSelector.updateDestinationSelection({
          list: feats.map((m) => m.values_.destinationId),
          listView: false,
        })
      );
    }
  }

  adjustLayersToCurrentPath(path) {
    if (path.toString().startsWith("/destinations")) {
      if (this.props.layers.destinationLayerVisibility === false) {
        this.props.dispatch(
          mapLayerSelector.changeDestinationLayerVisibility(true)
        );
      }
      DestinationsLayer.setVisible(true);
      if (this.props.layers.siteLayerVisibility === true) {
        this.props.dispatch(mapLayerSelector.changeSiteLayerVisibility(false));
      }
      SiteLayer.setVisible(false);
    }
    if (path.toString().startsWith("/sites")) {
      if (this.props.layers.destinationLayerVisibility === true) {
        this.props.dispatch(
          mapLayerSelector.changeDestinationLayerVisibility(false)
        );
      }
      DestinationsLayer.setVisible(false);
      if (this.props.layers.siteLayerVisibility === false) {
        this.props.dispatch(mapLayerSelector.changeSiteLayerVisibility(true));
      }
      SiteLayer.setVisible(true);
    }
  }

  createLayerSwapperButton() {
    const layerSelectionButton = document.createElement("button");
    layerSelectionButton.setAttribute("id", "layerManagerMapButton");
    layerSelectionButton.setAttribute("name", "layerManagerMapButton");
    layerSelectionButton.setAttribute(
      "aria-label",
      this.props.t("open_layers_button")
    );
    layerSelectionButton.setAttribute(
      "title",
      this.props.t("layer_manager_button_tooltip")
    );
    layerSelectionButton.className = "btn btn-primary map-manager-button";
    layerSelectionButton.hidden = !this.props.layers.showMapManager;

    const image = document.createElement("img");
    image.setAttribute("src", layers_stacked_fill);
    image.setAttribute("alt", "");

    layerSelectionButton.appendChild(image);
    layerSelectionButton.addEventListener("click", this.swapMapManager);

    layerSelectionControl.element.replaceChildren();
    layerSelectionControl.element.appendChild(layerSelectionButton);

    const tooltip = document.createElement("tooltip");
    tooltip.setAttribute(
      "id",
      "tooltip-" + this.props.t("layer_manager_button_tooltip")
    );
    tooltip.setAttribute("noArrow", "");
    tooltip.style.zIndex = 50000;
    tooltip.style.fontSize = "small";
    layerSelectionControl.element.appendChild(tooltip);
    layerSelectionControl.className = "ol-unselectable ol-control";

    layerSelectionControl.setMap(this.baseMap);
  }

  createResetButton() {
    const resetButton = document.createElement("button");
    resetButton.setAttribute("id", "layerResetButton");
    resetButton.setAttribute("name", "layerResetButton");
    resetButton.setAttribute("aria-label", this.props.t("layer_reset_button"));
    resetButton.setAttribute(
      "title",
      this.props.t("layer_reset_button_tooltip")
    );
    resetButton.className = "btn btn-primary map-manager-button";

    const image = document.createElement("img");
    image.setAttribute("src", eraser2);
    image.setAttribute("alt", "");

    resetButton.appendChild(image);
    resetButton.addEventListener("click", this.resetLayers);

    layerResetControl.element.replaceChildren();
    layerResetControl.element.appendChild(resetButton);

    const tooltip = document.createElement("tooltip");
    tooltip.setAttribute(
      "id",
      "tooltip-" + this.props.t("layer_reset_button_tooltip")
    );
    tooltip.setAttribute("noArrow", "");
    tooltip.style.zIndex = 50000;
    tooltip.style.fontSize = "small";
    layerResetControl.element.appendChild(tooltip);
    layerResetControl.className = "ol-unselectable ol-control";

    layerResetControl.setMap(this.baseMap);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.location.pathname !== this.state.lastAdderess) {
      this.setState({ lastAdderess: this.props.location.pathname }, () =>
        this.adjustLayersToCurrentPath(this.props.location.pathname)
      );
    }

    if (
      regionalUnitsLayerSource.getFeatures().length !== 0 &&
      parkDistrictLayerSource.getFeatures().length !== 0 &&
      entityLayerSource.getFeatures().length !== 0 &&
      landUseUnitSource.getFeatures().length !== 0 &&
      countiesLayerSource.getFeatures().length !== 0 &&
      municipalitiesLayerSource.getFeatures().length !== 0 &&
      this.state.layersLoaded === false
    ) {
      this.setState({ layersLoaded: true });
      this.updateDestinationLayerWithFilters();
      this.updateSiteLayerWithFilters();
    }

    if (
      this.props.showAddTooling !== "undefined" &&
      this.props.showAddTooling === false
    ) {
      this.baseMap.getViewport().className = this.baseMap
        .getViewport()
        .className.replace("custom-cursor", "");
    }
  }

  async componentDidMount() {
    let searchParamEntries = {};
    for (let entry of this.props.searchParams.entries()) {
      searchParamEntries[entry[0]] = entry[1] === "true";
    }

    try {
      if (
        !this.props.storeHelper.getState().codesSelector.codes.codeClassesLoaded
      ) {
        let response = await GetCodeClasses();

        if (response instanceof AxiosError) {
          this.setState({ error: response });
          this.props.dispatch(codesSelector.changeCodeClassesLoaded(false));
        } else {
          this.props.dispatch(codesSelector.fillCodeClasses(response));
          this.props.dispatch(codesSelector.changeCodeClassesLoaded(true));
        }
      }
    } catch (error) {
      console.error(error);
    }

    this.baseMap.setTarget("map-container");

    if (document.getElementById("layerResetButton") === null) {
      this.createResetButton();
    } else {
      var element = document.getElementById("layerResetButton");
      if (typeof element !== "undefined" && element !== null) {
        element.parentNode.removeChild(element);
        this.createResetButton();
      }
    }

    if (document.getElementById("layerManagerMapButton") === null) {
      this.createLayerSwapperButton();
    }

    let basemapAdded = false;
    this.baseMap.getLayers().forEach((f) => {
      if (f.get("name") === baseMapLayerName) {
        basemapAdded = true;
      }
    });

    if (basemapAdded === false) {
      this.baseMap.addLayer(this.setBaseMapLayer());
      this.baseMap.on("singleclick", this.handleMapClick);
      this.baseMap.on("contextmenu", this.handleRightClick);
    }

    this.loadDestinations();
    this.loadSites();

    let testSelectInteraction = false;
    this.baseMap.getInteractions().forEach((f) => {
      if (f instanceof Select) {
        testSelectInteraction = true;
      }
    });

    if (testSelectInteraction === false) {
      this.selectSingleClick.on("select", () => {
        let features = this.selectSingleClick.getFeatures();

        if (features.getLength() > 0) {
          let point = features
            .getArray()
            .find((f) => f.getGeometry().getType() === "Point");

          if (typeof point !== "undefined") {
            this.baseMap
              .getView()
              .fit(point.getGeometry(), { padding: [170, 50, 30, 150] });
          } else {
            var extent = features.item(0).getGeometry().getExtent().slice(0);
            features.forEach(function (feature) {
              olExtent.extend(extent, feature.getGeometry().getExtent());
            });
            this.baseMap.getView().fit(extent, { padding: [170, 50, 30, 150] });
          }
        }

        this.updateDestinationLayerWithFilters();
        this.updateSiteLayerWithFilters();
      });

      this.baseMap.addInteraction(this.selectSingleClick);
    }
    if (
      this.props.storeHelper.getState().mapCommonsSelector.map.regionalUnits
        .length === 0 &&
      this.props.storeHelper.getState().mapCommonsSelector.map.parkDistricts
        .length === 0 &&
      this.props.storeHelper.getState().mapCommonsSelector.map.entities
        .length === 0 &&
      this.props.storeHelper.getState().mapCommonsSelector.map.landUseUnits
        .length === 0 &&
      this.props.storeHelper.getState().mapCommonsSelector.map.counties
        .length === 0 &&
      this.props.storeHelper.getState().mapCommonsSelector.map.municipalities
        .length === 0
    ) {
      let regionalUnitsPromise = new Promise((resolve) =>
        resolve(GetRegionalUnits())
      );
      let parkDistrictPromise = new Promise((resolve) =>
        resolve(GetParkDistricts())
      );
      let entitiesPromise = new Promise((resolve) => resolve(GetEntities()));
      let landUseUnitsPromise = new Promise((resolve) =>
        resolve(GetLandUseUnits())
      );
      let countyPromise = new Promise((resolve) => resolve(GetCounties()));
      let municipalityPromise = new Promise((resolve) =>
        resolve(GetMunicipalities())
      );

      let responses = await Promise.all([
        regionalUnitsPromise,
        parkDistrictPromise,
        entitiesPromise,
        landUseUnitsPromise,
        countyPromise,
        municipalityPromise,
      ]);

      let regionalUnitsResponse = responses[0];
      let parkDistrictResponse = responses[1];
      let entitiesResponse = responses[2];
      let landUseUnitsResponse = responses[3];

      let countyResponse = responses[4];
      let municipalityResponse = responses[5];

      if (regionalUnitsResponse instanceof AxiosError) {
        console.error(
          "Failed to gather regional units: " + regionalUnitsResponse
        );
      } else if (this.state.layersLoaded === false) {
        let geoJsonFormat = new GeoJSON();

        if (regionalUnitsResponse.data.length > 0) {
          this.props.dispatch(
            mapCommonsSelector.updateRegionalUnits(
              regionalUnitsResponse.data.map((x) =>
                geoJsonFormat.readFeature(x)
              )
            )
          );
        }
      }

      if (parkDistrictResponse instanceof AxiosError) {
        console.error(
          "Failed to gather parkdistricts: " + parkDistrictResponse
        );
      } else if (this.state.layersLoaded === false) {
        let geoJsonFormat = new GeoJSON();

        if (parkDistrictResponse.data.length > 0) {
          this.props.dispatch(
            mapCommonsSelector.updateParkDistricts(
              parkDistrictResponse.data.map((x) => geoJsonFormat.readFeature(x))
            )
          );
        }
      }

      if (entitiesResponse instanceof AxiosError) {
        console.error("Failed to gather entities: " + entitiesResponse);
      } else if (this.state.layersLoaded === false) {
        let geoJsonFormat = new GeoJSON();

        if (entitiesResponse.data.length > 0) {
          this.props.dispatch(
            mapCommonsSelector.updateEntities(
              entitiesResponse.data.map((x) => geoJsonFormat.readFeature(x))
            )
          );
        }
      }

      if (landUseUnitsResponse instanceof AxiosError) {
        console.error("Failed to gather landuseunits: " + landUseUnitsResponse);
      } else if (this.state.layersLoaded === false) {
        let geoJsonFormat = new GeoJSON();

        if (landUseUnitsResponse.data.length > 0) {
          this.props.dispatch(
            mapCommonsSelector.updateLandUseUnits(
              landUseUnitsResponse.data
                .filter((x) => x.properties.InUse)
                .map((x) => geoJsonFormat.readFeature(x))
            )
          );
        }
      }

      if (countyResponse instanceof AxiosError) {
        console.error("Failed to gather counties: " + countyResponse);
      } else if (this.state.layersLoaded === false) {
        let geoJsonFormat = new GeoJSON();

        if (countyResponse.data.length > 0) {
          this.props.dispatch(
            mapCommonsSelector.updateCounties(
              countyResponse.data.map((x) => geoJsonFormat.readFeature(x))
            )
          );
        }
      }

      if (municipalityResponse instanceof AxiosError) {
        console.error(
          "Failed to gather municipalities: " + municipalityResponse
        );
      } else if (this.state.layersLoaded === false) {
        let geoJsonFormat = new GeoJSON();

        if (municipalityResponse.data.length > 0) {
          this.props.dispatch(
            mapCommonsSelector.updateMunicipalities(
              municipalityResponse.data.map((x) => geoJsonFormat.readFeature(x))
            )
          );
        }
      }

      if (
        regionalUnitsLayerSource.getFeatures().length === 0 &&
        parkDistrictLayerSource.getFeatures().length === 0 &&
        entityLayerSource.getFeatures().length === 0 &&
        landUseUnitSource.getFeatures().length === 0 &&
        countiesLayerSource.getFeatures().length === 0 &&
        municipalitiesLayerSource.getFeatures().length === 0
      ) {
        if (
          this.props.storeHelper.getState().mapCommonsSelector.map.regionalUnits
            .length > 0
        ) {
          const batchSize = 10; // Adjust this value based on your performance needs
          const features =
            this.props.storeHelper.getState().mapCommonsSelector.map
              .regionalUnits;

          for (let i = 0; i < features.length; i += batchSize) {
            const batch = features.slice(i, i + batchSize);
            regionalUnitsLayerSource.addFeatures(batch);
          }
        }
        if (
          this.props.storeHelper.getState().mapCommonsSelector.map.parkDistricts
            .length > 0
        ) {
          const batchSize = 10; // Adjust this value based on your performance needs
          const features =
            this.props.storeHelper.getState().mapCommonsSelector.map
              .parkDistricts;

          for (let i = 0; i < features.length; i += batchSize) {
            const batch = features.slice(i, i + batchSize);
            parkDistrictLayerSource.addFeatures(batch);
          }
        }
        if (
          this.props.storeHelper.getState().mapCommonsSelector.map.entities
            .length > 0
        ) {
          const batchSize = 10; // Adjust this value based on your performance needs
          const features =
            this.props.storeHelper.getState().mapCommonsSelector.map.entities;

          for (let i = 0; i < features.length; i += batchSize) {
            const batch = features.slice(i, i + batchSize);
            entityLayerSource.addFeatures(batch);
          }
        }

        if (
          this.props.storeHelper.getState().mapCommonsSelector.map.landUseUnits
            .length > 0
        ) {
          const batchSize = 700; // Adjust this value based on your performance needs
          const features =
            this.props.storeHelper.getState().mapCommonsSelector.map
              .landUseUnits;

          for (let i = 0; i < features.length; i += batchSize) {
            const batch = features.slice(i, i + batchSize);
            landUseUnitSource.addFeatures(batch);
          }
        }

        if (
          this.props.storeHelper.getState().mapCommonsSelector.map.counties
            .length > 0
        ) {
          const batchSize = 10; // Adjust this value based on your performance needs
          const features =
            this.props.storeHelper.getState().mapCommonsSelector.map.counties;

          for (let i = 0; i < features.length; i += batchSize) {
            const batch = features.slice(i, i + batchSize);
            countiesLayerSource.addFeatures(batch);
          }
        }

        if (
          this.props.storeHelper.getState().mapCommonsSelector.map
            .municipalities.length > 0
        ) {
          const batchSize = 50; // Adjust this value based on your performance needs
          const features =
            this.props.storeHelper.getState().mapCommonsSelector.map
              .municipalities;

          for (let i = 0; i < features.length; i += batchSize) {
            const batch = features.slice(i, i + batchSize);
            municipalitiesLayerSource.addFeatures(batch);
          }
        }
      }

      //Handle mapselections in local storage
      const alllayers = [
        "counties",
        "municipalities",
        "regionalUnits",
        "parkDistricts",
        "entities",
        "landUseUnitsStroke",
        "landUseUnitsFill",
      ];

      alllayers.forEach((layer) => {
        switch (layer) {
          case "counties":
            this.handleLayerVisibility(
              "counties",
              this.props.storeHelper.getState().layerSelector.layers[
                "countiesLayerVisibility"
              ],
              true
            );
            break;
          case "municipalities":
            this.handleLayerVisibility(
              "municipalities",
              this.props.storeHelper.getState().layerSelector.layers[
                "municipalitiesLayerVisibility"
              ],
              true
            );
            break;
          case "regionalUnits":
            this.handleLayerVisibility(
              "regionalUnits",
              this.props.storeHelper.getState().layerSelector.layers[
                "regionalUnitsLayerVisibility"
              ],
              true
            );
            break;
          case "parkDistricts":
            this.handleLayerVisibility(
              "parkDistricts",
              this.props.storeHelper.getState().layerSelector.layers[
                "parkDistrictsLayerVisibility"
              ],
              true
            );
            break;
          case "entities":
            this.handleLayerVisibility(
              "entities",
              this.props.storeHelper.getState().layerSelector.layers[
                "entitiesLayerVisibility"
              ],
              true
            );
            break;
          case "landUseUnitsStroke":
            this.handleLayerVisibility(
              "landUseUnitsStroke",
              this.props.storeHelper.getState().layerSelector.layers[
                "landUseUnitLayerStrokeVisibility"
              ],
              true
            );
            break;
          case "landUseUnitsFill":
            this.handleLayerVisibility(
              "landUseUnitsFill",
              this.props.storeHelper.getState().layerSelector.layers[
                "landUseUnitLayerFillVisibility"
              ],
              true
            );
            break;
          default:
            console.warn(`Unknown layer: ${layer}`);
        }
      });

      this.setState({
        mapLoaded: true,
        layersLoaded: true,
      });
    }
  }

  addPlace() {
    this.setState({ addPlaceMode: !this.state.addPlaceMode });
    if (typeof this.baseMap !== "undefined") {
      this.baseMap.getViewport().className += " custom-cursor";
      // this.baseMap.getViewport().style.cursor = 'crosshair';

      this.setState({ cursorAddMode: true });
    }
  }

  handleLayerVisibility(layer, isVisible, initial = false) {
    switch (layer) {
      case "destinationLayer":
        DestinationsLayer.setVisible(isVisible);
        if (!initial) {
          this.props.dispatch(
            mapLayerSelector.changeDestinationLayerVisibility()
          );
        }
        break;
      case "siteLayer":
        SiteLayer.setVisible(isVisible);
        if (!initial) {
          this.props.dispatch(mapLayerSelector.changeSiteLayerVisibility());
        }
        break;
      case "counties":
        if (!initial) {
          this.props.dispatch(mapLayerSelector.changeCountiesLayerVisibility());
        }
        let cStyle = countiesLayer.getStyle();
        cStyle.getStroke().setColor([124, 252, 0, isVisible ? 1 : 0]);
        countiesLayer.setStyle(cStyle);
        break;
      case "municipalities":
        if (!initial) {
          this.props.dispatch(
            mapLayerSelector.changeMunicipalitiesLayerVisibility()
          );
        }
        let mStyle = municipalitiesLayer.getStyle();
        mStyle.getStroke().setColor([0, 0, 0, isVisible ? 1 : 0]);
        municipalitiesLayer.setStyle(mStyle);
        break;
      case "regionalUnits":
        if (!initial) {
          this.props.dispatch(
            mapLayerSelector.changeRegionalUnitsLayerVisibility()
          );
        }
        let regStyle = regionalUnitsLayer.getStyle();
        regStyle.getStroke().setColor([0, 43, 255, isVisible ? 1 : 0]);
        regionalUnitsLayer.setStyle(regStyle);
        break;
      case "parkDistricts":
        if (!initial) {
          this.props.dispatch(
            mapLayerSelector.changeParkDistrictsLayerVisibility()
          );
        }
        let parkStyle = parkDistrictLayer.getStyle();
        parkStyle.getStroke().setColor([78, 88, 39, isVisible ? 1 : 0]);
        parkDistrictLayer.setStyle(parkStyle);
        break;
      case "entities":
        if (!initial) {
          this.props.dispatch(mapLayerSelector.changeEntitiesLayerVisibility());
        }
        let entityStyle = entitiesLayer.getStyle();
        entityStyle.getStroke().setColor([228, 255, 0, isVisible ? 1 : 0]);
        entitiesLayer.setStyle(entityStyle);
        break;
      case "landUseUnitsStroke":
        if (!initial) {
          this.props.dispatch(
            mapLayerSelector.changeLandUseUnitLayerStrokeVisibility()
          );
        }
        let landUseUnitStrokeStyle = landUseUnitLayer.getStyle();
        landUseUnitStrokeStyle
          .getStroke()
          .setColor([3, 179, 40, isVisible ? 1 : 0]);
        landUseUnitLayer.setStyle(landUseUnitStrokeStyle);
        break;
      case "landUseUnitsFill":
        if (!initial) {
          this.props.dispatch(
            mapLayerSelector.changeLandUseUnitLayerFillVisibility()
          );
        }
        let landUseUnitFillStyle = landUseUnitLayer.getStyle();
        landUseUnitFillStyle
          .getFill()
          .setColor([3, 179, 40, isVisible ? 0.2 : 0]);
        landUseUnitLayer.setStyle(landUseUnitFillStyle);
        break;

      default:
        console.error("Problems to find layer to change visibility");
    }
  }

  handleSiteFilter(selection) {
    this.props.dispatch(sitesFilterSelector.changeSiteSelection(selection));
    this.updateSiteLayerWithFilters();
  }

  handleDestinationFilter(selection) {
    this.props.dispatch(
      destinationsFilterSelector.changeDestinationSelection(selection)
    );
    this.updateDestinationLayerWithFilters();
  }

  handleDestinationTypeFilter(selection) {
    this.props.dispatch(
      destinationsFilterSelector.changeDestinationTypeSelection(selection)
    );
    this.updateDestinationLayerWithFilters();
  }

  handleDestinationFunderFilter(selection) {
    this.props.dispatch(
      destinationsFilterSelector.changeDestinationFunderSelection(selection)
    );
    this.updateDestinationLayerWithFilters();
  }

  handleDestinationOwnerFilter(selection) {
    this.props.dispatch(
      destinationsFilterSelector.changeDestinationOwnerSelection(selection)
    );
    this.updateDestinationLayerWithFilters();
  }

  handleDestinationLandUseUnitFilter(selection) {
    this.props.dispatch(
      destinationsFilterSelector.changeDestinationLandUseUnitCodeSelection(
        selection
      )
    );
    this.updateDestinationLayerWithFilters();
  }

  getCodeClassRelatedFilterNames(codeClassNumber) {
    let codeClass = this.props.storeHelper
      .getState()
      .codesSelector.codes.codeClasses.find(
        (cc) => cc.codeClassId === codeClassNumber
      );
    let choiceArray = codeClass.codes.map((code) => {
      if (
        typeof code !== "undefined" &&
        typeof code.languages[
          code.languages.findIndex(
            (i) => i.languageName === this.props.t("common:GMLLanguage")
          )
        ] !== "undefined" &&
        typeof code.codeId !== "undefined"
      )
        return {
          id: code.codeId,
          name: code.languages[
            code.languages.findIndex(
              (i) => i.languageName === this.props.t("common:GMLLanguage")
            )
          ].text,
        };
    });

    return choiceArray;
  }

  getCodeClassRelatedFilterIds(codeClassNumber) {
    let codeClass = this.props.storeHelper
      .getState()
      .codesSelector.codes.codeClasses.find(
        (cc) => cc.codeClassId === codeClassNumber
      );
    if (typeof codeClass !== "undefined") {
      let choiceArray = codeClass.codes.map((code) => {
        if (
          typeof code !== "undefined" &&
          typeof code.languages[
            code.languages.findIndex(
              (i) => i.languageName === this.props.t("common:GMLLanguage")
            )
          ] !== "undefined" &&
          typeof code.codeId !== "undefined"
        )
          return {
            id: code.codeId,
          };
      });
      return choiceArray;
    }
    return null;
  }

  render() {
    let { t } = this.props;
    return (
      <>
        <div className="map-manager" id="map-layer-manager" role={"region"}>
          {this.props.layers.showMapManager && (
            <div className={"accordion"}>
              <div className="layer-selector-closer">
                <button
                  className={"btn-no-style"}
                  type={"button"}
                  id={"accordiondMapManager"}
                  onClick={this.swapMapManager}
                >
                  <img
                    src={close_fill}
                    alt={"Close layer selection"}
                    id="layer-selection-closer"
                  />
                </button>
              </div>
              <div
                aria-expanded={this.props.layers.isLayerSelectionOpen}
                type={"button"}
                aria-controls={"accordionLayerSelection"}
                id={"accordionLayerSelection"}
                className="no-wrap-plus-minus-signs show-cursor"
                onClick={() => {
                  this.props.dispatch(
                    mapLayerSelector.changeOpenLayerSelection()
                  );
                }}
              >
                <div>{this.props.t("layers_header")}</div>
                {this.props.layers.isLayerSelectionOpen === false ? (
                  <img
                    src={add_fill}
                    alt=""
                    className="sign-placement show-cursor"
                  />
                ) : (
                  <img
                    src={subtract_fill}
                    alt=""
                    className="sign-placement show-cursor"
                  />
                )}
              </div>
              {this.props.layers.isLayerSelectionOpen === true ? (
                <div
                  aria-labelledby={"accordionLayerSelection"}
                  onClick={this.clearMapSelection}
                  className="basemap-layer-component show-cursor font-to-opened-filter-selection"
                  onChange={(event) => {
                    this.handleLayerVisibility(
                      event.target.value,
                      event.target.checked
                    );
                  }}
                >
                  <div>
                    <input
                      className="drop-checkbox show-cursor"
                      type="checkbox"
                      id={"regionalUnits"}
                      value="regionalUnits"
                      checked={this.props.layers.regionalUnitsLayerVisibility}
                    />{" "}
                    <label className="show-cursor" htmlFor={"regionalUnits"}>
                      {this.props.t("regionalUnits_layer")}
                    </label>
                  </div>
                  <div>
                    <input
                      className="drop-checkbox show-cursor"
                      type="checkbox"
                      id={"parkDistricts"}
                      value="parkDistricts"
                      checked={this.props.layers.parkDistrictsLayerVisibility}
                    />{" "}
                    <label className="show-cursor" htmlFor={"parkDistricts"}>
                      {this.props.t("parkDistrict_layer")}
                    </label>
                  </div>
                  <div>
                    <input
                      className="drop-checkbox show-cursor"
                      type="checkbox"
                      value="entities"
                      id={"entities"}
                      checked={this.props.layers.entitiesLayerVisibility}
                    />{" "}
                    <label className="show-cursor" for="entities">
                      {this.props.t("entities_layer")}
                    </label>
                  </div>
                  <div>
                    <input
                      className="drop-checkbox show-cursor"
                      type="checkbox"
                      id={"landUseUnitsStroke"}
                      value="landUseUnitsStroke"
                      checked={
                        this.props.layers.landUseUnitLayerStrokeVisibility
                      }
                    />{" "}
                    <label
                      className="show-cursor"
                      htmlFor={"landUseUnitsStroke"}
                    >
                      {this.props.t("landUseUnitStroke_layer")}
                    </label>
                  </div>
                  <div>
                    <input
                      className="drop-checkbox show-cursor"
                      type="checkbox"
                      id={"landUseUnitsFill"}
                      value="landUseUnitsFill"
                      checked={this.props.layers.landUseUnitLayerFillVisibility}
                    />{" "}
                    <label className="show-cursor" htmlFor={"landUseUnitsFill"}>
                      {this.props.t("landUseUnitFill_layer")}
                    </label>
                  </div>
                  <div>
                    <input
                      className="drop-checkbox show-cursor"
                      type="checkbox"
                      value="counties"
                      id={"counties"}
                      checked={this.props.layers.countiesLayerVisibility}
                    />{" "}
                    <label className="show-cursor" htmlFor={"counties"}>
                      {this.props.t("counties_layer")}
                    </label>
                  </div>
                  <div>
                    <input
                      className="drop-checkbox show-cursor"
                      type="checkbox"
                      id={"municipalities"}
                      value="municipalities"
                      checked={this.props.layers.municipalitiesLayerVisibility}
                    />{" "}
                    <label className="show-cursor" htmlFor={"municipalities"}>
                      {this.props.t("municipalities_layer")}
                    </label>
                  </div>
                </div>
              ) : null}
              <hr />
              <div className="filter-header no-wrap-plus-minus-signs">
                {this.props.t("filters_header")}
              </div>

              <div
                aria-expanded={this.props.destinationFilters.isOpen}
                className="no-wrap-plus-minus-signs show-cursor"
                id={"accordionDestinationFilters"}
                onClick={() => {
                  this.props.dispatch(
                    destinationsFilterSelector.changeIsOpen()
                  );
                }}
              >
                <div>{this.props.t("destination_filters_header")}</div>
                {this.props.destinationFilters.isOpen === false ? (
                  <img
                    src={add_fill}
                    alt="Open layers selection"
                    className="sign-placement"
                  />
                ) : (
                  <img
                    src={subtract_fill}
                    alt="Close layers selection"
                    className="sign-placement"
                  />
                )}
              </div>
              <div>
                {this.props.destinationFilters.isOpen ? (
                  <>
                    <div
                      aria-expanded={this.props.destinationFilters.isOpen}
                      aria-controls={"accordionDestinationFilters"}
                      id={"accordionDestinationFilters"}
                      className="layers-title font-to-opened-filter-selection no-wrap-plus-minus-signs show-cursor"
                      onClick={() => {
                        this.handleLayerVisibility(
                          "destinationLayer",
                          !this.props.storeHelper.getState().layerSelector
                            .layers.destinationLayerVisibility
                        );
                      }}
                    >
                      <div>{this.props.t("destination_filters_header")}</div>
                      {this.props.storeHelper.getState().layerSelector.layers
                        .destinationLayerVisibility === true ? (
                        <img
                          src={view_fill}
                          alt="Open layers selection"
                          className="sign-placement"
                        />
                      ) : (
                        <img
                          src={view_off_fill}
                          alt="Close layers selection"
                          className="sign-placement"
                        />
                      )}
                    </div>

                    <div
                      aria-labelledby={"accordionDestinationFilters"}
                      className={
                        "layers-title no-wrap-plus-minus-signs show-cursor"
                      }
                    >
                      <button
                        className={"btn-no-style"}
                        aria-expanded={
                          this.props.destinationFilters.destination.isOpen
                        }
                        aria-controls={"accordionDestinationFiltersDestination"}
                        id={"accordionDestinationFiltersDestination"}
                        onClick={() => {
                          this.props.dispatch(
                            destinationsFilterSelector.changeDestinationIsOpen()
                          );
                        }}
                      >
                        {this.props.t("destination_validity_filter_header")}
                      </button>
                      <div className="flex-for-plus-and-checkbox">
                        <label>
                          {" "}
                          {this.props.destinationFilters.destination.isOpen ===
                          false ? (
                            <img
                              src={add_fill}
                              alt={t("open_layers_selection")}
                              className="sign-placement show-cursor"
                              onClick={() => {
                                this.props.dispatch(
                                  destinationsFilterSelector.changeDestinationIsOpen()
                                );
                              }}
                            />
                          ) : (
                            <img
                              src={subtract_fill}
                              alt={t("close_layer_selection")}
                              className="sign-placement show-cursor"
                              onClick={() => {
                                this.props.dispatch(
                                  destinationsFilterSelector.changeDestinationIsOpen()
                                );
                              }}
                            />
                          )}
                        </label>
                      </div>
                    </div>
                    {this.props.destinationFilters.destination.isOpen ? (
                      <div
                        className="destination-status-box font-to-opened-filter-selection show-cursor"
                        onChange={(event) => {
                          this.handleDestinationFilter(event.target.value);
                        }}
                      >
                        <div>
                          <input
                            className="drop-checkbox show-cursor"
                            type="checkbox"
                            id={"destinationInUseFilterAll"}
                            value="destinationInUseFilterAll"
                            onClick={() => {
                              this.props.dispatch(
                                destinationsFilterSelector.changeDestinationSelectAll()
                              );
                              this.updateDestinationLayerWithFilters();
                            }}
                            checked={
                              this.props.storeHelper.getState()
                                .destinationsFilterSelector.destination
                                .selectAll
                            }
                          />
                          <label
                            className="show-cursor"
                            htmlFor={"destinationInUseFilterAll"}
                          >
                            {" "}
                            {this.props.t("select_all")}
                          </label>
                        </div>

                        <div>
                          <input
                            className="drop-checkbox show-cursor"
                            type="checkbox"
                            value="inUse"
                            id={"inUse"}
                            checked={
                              this.props.destinationFilters.destination.inUse
                            }
                          />
                          <label htmlFor={"inUse"} className="show-cursor">
                            {" "}
                            {this.props.t("destination_filters_insUse")}
                          </label>
                        </div>
                        <div>
                          <input
                            className="drop-checkbox show-cursor"
                            type="checkbox"
                            id={"notInUse"}
                            value="notInUse"
                            checked={
                              this.props.destinationFilters.destination.notInUse
                            }
                          />
                          <label htmlFor={"notInUse"} className="show-cursor">
                            {" "}
                            {this.props.t("destination_filters_notInsUse")}
                          </label>
                        </div>
                      </div>
                    ) : null}

                    <div className="layers-title show-cursor">
                      <div
                        onClick={() => {
                          this.props.dispatch(
                            destinationsFilterSelector.changeDestinationTypeIsOpen()
                          );
                        }}
                      >
                        {this.props.t("destination_type_filter_header")}
                      </div>
                      <div className="flex-for-plus-and-checkbox show-cursor">
                        <label
                          className="show-cursor"
                          htmlFor={"typeFilterAll"}
                        >
                          {this.props.destinationFilters.destinationType
                            .isOpen === false ? (
                            <img
                              src={add_fill}
                              alt="Open layers selection"
                              className="sign-placement show-cursor"
                              onClick={() => {
                                this.props.dispatch(
                                  destinationsFilterSelector.changeDestinationTypeIsOpen()
                                );
                              }}
                            />
                          ) : (
                            <img
                              src={subtract_fill}
                              alt="Close layers selection"
                              className="sign-placement show-cursor"
                              onClick={() => {
                                this.props.dispatch(
                                  destinationsFilterSelector.changeDestinationTypeIsOpen()
                                );
                              }}
                            />
                          )}
                        </label>
                      </div>
                    </div>
                    {this.props.destinationFilters.destinationType.isOpen ? (
                      <div
                        className="font-to-opened-filter-selection show-cursor"
                        onChange={(event) => {
                          this.handleDestinationTypeFilter(event.target.value);
                        }}
                      >
                        <div>
                          <input
                            className="drop-checkbox show-cursor"
                            type="checkbox"
                            id={"typeFilterAll"}
                            value="typeFilterAll"
                            onClick={() => {
                              this.props.dispatch(
                                destinationsFilterSelector.changeDestinationTypeSelectAll()
                              );
                              this.updateDestinationLayerWithFilters();
                            }}
                            checked={
                              this.props.destinationFilters.destinationType
                                .selectAll
                            }
                          />{" "}
                          <label
                            className="show-cursor"
                            htmlFor={"typeFilterAll"}
                          >
                            {" "}
                            {this.props.t("select_all")}
                          </label>
                        </div>
                        {this.getCodeClassRelatedFilterNames(
                          1001,
                          "typeCode"
                        ).map((i) => {
                          return (
                            <div>
                              <input
                                className="drop-checkbox show-cursor"
                                id={"typeCode_" + i.id}
                                type="checkbox"
                                value={"typeCode_" + i.id}
                                checked={
                                  this.props.destinationFilters.destinationType[
                                    "typeCode_" + i.id
                                  ]
                                }
                              />{" "}
                              <label
                                className="show-cursor"
                                htmlFor={"typeCode_" + i.id}
                              >
                                {i.id + " " + i.name}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}

                    <div className="layers-title show-cursor">
                      <div
                        onClick={() => {
                          this.props.dispatch(
                            destinationsFilterSelector.changeDestinationOwnerCodeIsOpen()
                          );
                        }}
                      >
                        {this.props.t("destination_owner_filter_header")}
                      </div>
                      <div className="flex-for-plus-and-checkbox show-cursor">
                        <label>
                          {" "}
                          {!this.props.destinationFilters.destinationOwner
                            .isOpen ? (
                            <img
                              src={add_fill}
                              alt="Open layers selection"
                              className="sign-placement show-cursor"
                              onClick={() => {
                                this.props.dispatch(
                                  destinationsFilterSelector.changeDestinationOwnerCodeIsOpen()
                                );
                              }}
                            />
                          ) : (
                            <img
                              src={subtract_fill}
                              alt="Close layers selection"
                              className="sign-placement"
                              onClick={() => {
                                this.props.dispatch(
                                  destinationsFilterSelector.changeDestinationOwnerCodeIsOpen()
                                );
                              }}
                            />
                          )}
                        </label>
                      </div>
                    </div>
                    {this.props.destinationFilters.destinationOwner.isOpen ? (
                      <div
                        className="font-to-opened-filter-selection show-cursor"
                        onChange={(event) => {
                          this.handleDestinationOwnerFilter(event.target.value);
                        }}
                      >
                        <div>
                          <input
                            className="drop-checkbox show-cursor"
                            type="checkbox"
                            id={"ownerFilterAll"}
                            value="ownerFilterAll"
                            onClick={() => {
                              this.props.dispatch(
                                destinationsFilterSelector.changeDestinationOwnerCodeSelectAll()
                              );
                              this.updateDestinationLayerWithFilters();
                            }}
                            checked={
                              this.props.destinationFilters.destinationOwner
                                .selectAll
                            }
                          />{" "}
                          <label
                            className="show-cursor"
                            htmlFor={"ownerFilterAll"}
                          >
                            {" "}
                            {this.props.t("select_all")}
                          </label>
                        </div>
                        {this.getCodeClassRelatedFilterNames(
                          1002,
                          "ownerCode"
                        ).map((i) => {
                          return (
                            <div>
                              <input
                                className="drop-checkbox show-cursor"
                                type="checkbox"
                                id={"ownerCode_" + i.id}
                                value={"ownerCode_" + i.id}
                                checked={
                                  this.props.destinationFilters
                                    .destinationOwner["ownerCode_" + i.id]
                                }
                              />{" "}
                              <label
                                className="show-cursor"
                                htmlFor={"ownerCode_" + i.id}
                              >
                                {i.id + " " + i.name}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}

                    <div className="layers-title show-cursor">
                      <div
                        onClick={() => {
                          this.props.dispatch(
                            destinationsFilterSelector.changeDestinationFunderCodeIsOpen()
                          );
                        }}
                      >
                        {this.props.t("destination_funder_filter_header")}
                      </div>
                      <div className="flex-for-plus-and-checkbox show-cursor">
                        <label className="show-cursor">
                          {!this.props.destinationFilters.destinationFunder
                            .isOpen ? (
                            <img
                              src={add_fill}
                              alt="Open layers selection"
                              className="sign-placement"
                              onClick={() => {
                                this.props.dispatch(
                                  destinationsFilterSelector.changeDestinationFunderCodeIsOpen()
                                );
                              }}
                            />
                          ) : (
                            <img
                              src={subtract_fill}
                              alt="Close layers selection"
                              className="sign-placement"
                              onClick={() => {
                                this.props.dispatch(
                                  destinationsFilterSelector.changeDestinationFunderCodeIsOpen()
                                );
                              }}
                            />
                          )}
                        </label>
                      </div>
                    </div>
                    {this.props.destinationFilters.destinationFunder.isOpen ? (
                      <div
                        className="font-to-opened-filter-selection show-cursor"
                        onChange={(event) => {
                          this.handleDestinationFunderFilter(
                            event.target.value
                          );
                        }}
                      >
                        <div>
                          <input
                            className="drop-checkbox show-cursor"
                            type="checkbox"
                            id={"funderFilterAll"}
                            value="funderFilterAll"
                            onClick={() => {
                              this.props.dispatch(
                                destinationsFilterSelector.changeDestinationFunderCodeSelectAll()
                              );
                              this.updateDestinationLayerWithFilters();
                            }}
                            checked={
                              this.props.destinationFilters.destinationFunder
                                .selectAll
                            }
                          />{" "}
                          <label
                            className="show-cursor"
                            htmlFor={"funderFilterAll"}
                          >
                            {" "}
                            {this.props.t("select_all")}
                          </label>
                        </div>
                        {this.getCodeClassRelatedFilterNames(
                          1003,
                          "funderCode"
                        ).map((i) => {
                          return (
                            <div>
                              <input
                                className="drop-checkbox show-cursor"
                                type="checkbox"
                                id={"funderCode_" + i.id}
                                value={"funderCode_" + i.id}
                                checked={
                                  this.props.destinationFilters
                                    .destinationFunder["funderCode_" + i.id]
                                }
                              />{" "}
                              <label
                                className="show-cursor"
                                htmlFor={"funderCode_" + i.id}
                              >
                                {i.id + " " + i.name}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}

                    <div className="layers-title show-cursor">
                      <div
                        onClick={() => {
                          this.props.dispatch(
                            destinationsFilterSelector.changeDestinationLandUseUnitCodeIsOpen()
                          );
                        }}
                      >
                        {this.props.t(
                          "destination_land_use_unit_filter_header"
                        )}
                      </div>
                      <div className="flex-for-plus-and-checkbox show-cursor">
                        <label>
                          {!this.props.destinationFilters
                            .destinationLandUseUnitFilter.isOpen ? (
                            <img
                              src={add_fill}
                              alt="Open layers selection"
                              className="sign-placement show-cursor"
                              onClick={() => {
                                this.props.dispatch(
                                  destinationsFilterSelector.changeDestinationLandUseUnitCodeIsOpen()
                                );
                              }}
                            />
                          ) : (
                            <img
                              src={subtract_fill}
                              alt="Close layers selection"
                              className="sign-placement show-cursor"
                              onClick={() => {
                                this.props.dispatch(
                                  destinationsFilterSelector.changeDestinationLandUseUnitCodeIsOpen()
                                );
                              }}
                            />
                          )}
                        </label>
                      </div>
                    </div>
                    {this.props.destinationFilters.destinationLandUseUnitFilter
                      .isOpen ? (
                      <div
                        className="font-to-opened-filter-selection show-cursor"
                        onChange={(event) => {
                          this.handleDestinationLandUseUnitFilter(
                            event.target.value
                          );
                        }}
                      >
                        <div>
                          <input
                            className="drop-checkbox show-cursor"
                            type="checkbox"
                            id={"landUseUnitFilterAll"}
                            value="landUseUnitFilterAll"
                            onClick={() => {
                              this.props.dispatch(
                                destinationsFilterSelector.changeDestinationLandUseUnitCodeSelectAll()
                              );
                              this.updateDestinationLayerWithFilters();
                            }}
                            checked={
                              this.props.destinationFilters
                                .destinationLandUseUnitFilter.selectAll
                            }
                          />{" "}
                          <label
                            className="show-cursor"
                            htmlFor={"landUseUnitFilterAll"}
                          >
                            {" "}
                            {this.props.t("select_all")}
                          </label>
                        </div>
                        {this.getCodeClassRelatedFilterNames(
                          1011,
                          "landUseUnitCode"
                        ).map((i) => {
                          return (
                            <div>
                              <input
                                className="drop-checkbox show-cursor"
                                type="checkbox"
                                id={"landUseUnitCode_" + i.id}
                                value={"landUseUnitCode_" + i.id}
                                checked={
                                  this.props.destinationFilters
                                    .destinationLandUseUnitFilter[
                                    "landUseUnitCode_" + i.id
                                  ]
                                }
                              />{" "}
                              <label
                                className="show-cursor"
                                htmlFor={"landUseUnitCode_" + i.id}
                              >
                                {i.id + " " + i.name}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>

              <div
                aria-controls={"accordionSiteFilters"}
                id={"accordionSiteFilters"}
                className="no-wrap-plus-minus-signs show-cursor"
                onClick={() => {
                  this.props.dispatch(sitesFilterSelector.changeIsOpen());
                }}
              >
                <div>{this.props.t("site_filters_header")}</div>
                {!this.props.siteFilters.isOpen ? (
                  <img
                    src={add_fill}
                    alt="Open layers selection"
                    className="sign-placement"
                  />
                ) : (
                  <img
                    src={subtract_fill}
                    alt="Close layers selection"
                    className="sign-placement"
                  />
                )}
              </div>
              <div>
                {this.props.storeHelper.getState().sitesFilterSelector
                  .isOpen ? (
                  <>
                    <div
                      aria-labelledby={"accordionSiteFilters"}
                      className="layers-title font-to-opened-filter-selection no-wrap-plus-minus-signs show-cursor"
                      onClick={() => {
                        this.handleLayerVisibility(
                          "siteLayer",
                          !this.props.storeHelper.getState().layerSelector
                            .layers.siteLayerVisibility
                        );
                      }}
                    >
                      <div>{this.props.t("site_filters_header")}</div>
                      {this.props.storeHelper.getState().layerSelector.layers
                        .siteLayerVisibility ? (
                        <img
                          src={view_fill}
                          alt="Open layers selection"
                          className="sign-placement"
                        />
                      ) : (
                        <img
                          src={view_off_fill}
                          alt="Close layers selection"
                          className="sign-placement"
                        />
                      )}
                    </div>

                    <div
                      onClick={() => {
                        this.props.dispatch(
                          sitesFilterSelector.changeSiteIsOpen()
                        );
                      }}
                      aria-controls={"accordionSiteFiltersSite"}
                      id={"accordionSiteFiltersSite"}
                      className="no-wrap-plus-minus-signs show-cursor"
                    >
                      <label
                        className="show-cursor"
                        htmlFor={"accordionSiteFiltersSite"}
                      >
                        {this.props.t("site_status_filter_header")}
                      </label>
                      {!this.props.siteFilters.site.isOpen ? (
                        <img
                          src={add_fill}
                          alt="Open layers selection"
                          className="sign-placement"
                        />
                      ) : (
                        <img
                          src={subtract_fill}
                          alt="Close layers selection"
                          className="sign-placement"
                        />
                      )}
                    </div>
                    {this.props.storeHelper.getState().sitesFilterSelector.site
                      .isOpen ? (
                      <div
                        aria-labelledby={"accordionSiteFiltersSite"}
                        className="basemap-layer-component"
                      >
                        <div className="site-status-box">
                          <div>
                            <input
                              className="map-manager-checkboxes show-cursor"
                              type="checkbox"
                              id={"siteStatusFilterAll"}
                              value="siteStatusFilterAll"
                              onClick={() => {
                                this.props.dispatch(
                                  sitesFilterSelector.changeSiteSelectAll()
                                );
                                this.updateSiteLayerWithFilters();
                              }}
                              checked={this.props.siteFilters.site.selectAll}
                            />
                            <label htmlFor={"siteStatusFilterAll"}>
                              {this.props.t("select_all")}
                            </label>
                          </div>

                          <div
                            onChange={(event) => {
                              this.handleSiteFilter(event.target.value);
                            }}
                          >
                            <input
                              className="map-manager-checkboxes show-cursor"
                              type="checkbox"
                              id={"siteStatus_notSpecified"}
                              value="notSpecified"
                              checked={this.props.siteFilters.site.notSpecified}
                            />
                            <label htmlFor={"siteStatus_notSpecified"}>
                              {this.props.t("site_filters_notSpecified")}
                            </label>
                          </div>
                          <div
                            onChange={(event) => {
                              this.handleSiteFilter(event.target.value);
                            }}
                          >
                            <input
                              className="map-manager-checkboxes"
                              type="checkbox"
                              id={"siteStatus_planned"}
                              value="planned"
                              checked={this.props.siteFilters.site.planned}
                            />
                            <label htmlFor={"siteStatus_planned"}>
                              {this.props.t("site_filters_planned")}
                            </label>
                          </div>
                          <div
                            onChange={(event) => {
                              this.handleSiteFilter(event.target.value);
                            }}
                          >
                            <input
                              className="map-manager-checkboxes"
                              type="checkbox"
                              id={"siteStatus_underConstruction"}
                              value="underConstruction"
                              checked={
                                this.props.siteFilters.site.underConstruction
                              }
                            />
                            <label htmlFor={"siteStatus_underConstruction"}>
                              {this.props.t("site_filters_underConstruction")}
                            </label>
                          </div>
                          <div
                            onChange={(event) => {
                              this.handleSiteFilter(event.target.value);
                            }}
                          >
                            <input
                              className="map-manager-checkboxes"
                              type="checkbox"
                              value="inUse"
                              id={"siteStatus_inUse"}
                              checked={this.props.siteFilters.site.inUse}
                            />
                            <label htmlFor={"siteStatus_inUse"}>
                              {this.props.t("site_filters_inUse")}
                            </label>
                          </div>
                          <div
                            onChange={(event) => {
                              this.handleSiteFilter(event.target.value);
                            }}
                          >
                            <input
                              className="map-manager-checkboxes"
                              type="checkbox"
                              id={"siteStatus_discarded"}
                              value="discarded"
                              checked={this.props.siteFilters.site.discarded}
                            />
                            <label htmlFor={"siteStatus_discarded"}>
                              {this.props.t("site_filters_discarded")}
                            </label>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
              <hr />
              <div
                id={"accordionMapCommons"}
                aria-controls={"accordionMapCommons"}
                className="no-wrap-plus-minus-signs show-cursor"
                onClick={() => {
                  this.props.dispatch(
                    mapCommonsSelector.changeBasemapCommonsIsOpen()
                  );
                }}
              >
                <div>{this.props.t("background_maps_header")}</div>
                {!this.props.mapCommons.isOpen ? (
                  <img
                    src={add_fill}
                    alt="Open layers selection"
                    className="sign-placement"
                  />
                ) : (
                  <img
                    src={subtract_fill}
                    alt="Close layers selection"
                    className="sign-placement"
                  />
                )}
              </div>
              {this.props.mapCommons.isOpen ? (
                <div
                  aria-labelledby={"accordionMapCommons"}
                  className="basemap-selector-component show-cursor font-to-opened-filter-selection"
                  onChange={(event) => {
                    this.handleBaseMapChange(event.target.value);
                  }}
                >
                  <div>
                    <input
                      type="radio"
                      value="maastokartta"
                      name="basemap"
                      id={"maastokartta"}
                      checked={
                        this.props.mapCommons.map.mapSelection ===
                        "maastokartta"
                      }
                    />{" "}
                    <label className="show-cursor" htmlFor={"maastokartta"}>
                      {this.props.t("maastokartta")}
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      value="taustakartta"
                      name="basemap"
                      id={"taustakartta"}
                      checked={
                        this.props.mapCommons.map.mapSelection ===
                        "taustakartta"
                      }
                    />
                    <label className="show-cursor" htmlFor={"taustakartta"}>
                      {" "}
                      {this.props.t("taustakartta")}
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      value="selkokartta"
                      name="basemap"
                      id={"selkokartta"}
                      checked={
                        this.props.mapCommons.map.mapSelection === "selkokartta"
                      }
                    />
                    <label className="show-cursor" htmlFor={"selkokartta"}>
                      {" "}
                      {this.props.t("selkokartta")}
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      value="ortokuva"
                      name="basemap"
                      id={"ortokuva"}
                      checked={
                        this.props.mapCommons.map.mapSelection === "ortokuva"
                      }
                    />
                    <label className="show-cursor" htmlFor={"ortokuva"}>
                      {" "}
                      {this.props.t("ortokuva")}
                    </label>
                  </div>
                </div>
              ) : null}
            </div>
          )}
          <div
            style={{ width: this.props.layers.showMapManager ? "70%" : "100%" }}
          >
            <div>
              {typeof this.props.showAddTooling !== "undefined" &&
                this.props.showAddTooling === true &&
                typeof this.props.location !== "undefined" &&
                this.props.location.pathname === "/destinations/add" &&
                (this.state.layersLoaded === false ? (
                  <UploadingNotification
                    showModal={this.state.layersLoaded === false}
                  />
                ) : (
                  <DestinationToolbar addPlain={this.addPlace} />
                ))}
              {typeof this.props.showAddTooling !== "undefined" &&
                this.props.showAddTooling === true &&
                typeof this.props.location !== "undefined" &&
                this.props.location.pathname === "/sites/add" &&
                (this.state.layersLoaded === false ? (
                  <UploadingNotification
                    showModal={this.state.layersLoaded === false}
                  />
                ) : (
                  <SiteToolbar addPlain={this.addPlace} />
                ))}

              <div id="map-container" className="map-container"></div>
            </div>
          </div>
        </div>
      </>
    );
  }
}
export default withNavigation();
